import { useEffect, useState, useRef, Fragment } from "react";
import { refreshUprMap } from "@/shared/components/ProjRef";
import { useParams, Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { fmtErr, API } from "@/shared/lib/api";
import { PageHeader, Btn, Input, Field, Select } from "@/shared/components/UI";
import { ArrowLeft, Plus, X, Image as ImageIcon, Edit3, Trash2, Download, BarChart3, Truck, CheckCircle2, Check, Clock, Lock, Unlock, ExternalLink, Sparkles, Upload, AlertTriangle, Loader2, User, UserPlus, Layers, ChevronDown, IndianRupee } from "lucide-react";
import FileUpload from "@/shared/components/FileUpload";
import ClientReviewSection from "@/features/projects/components/ClientReviewSection";
import ProjectDCFolder from "@/features/projects/components/ProjectDCFolder";
import SiteMeasurementsTab from "@/features/projects/components/SiteMeasurementsTab";
import BoqScopePanel from "@/features/projects/components/BoqScopePanel";
import { toast } from "sonner";
// Tabs differ by role: admin+management see all; everyone else sees Overview + BOQ only
// Msg 954 — the POS tab (line-PO upload) and FILES tab (line-item file timeline)
// are removed from the project portal. POs are now raised in the "Purchase Orders"
// sidebar; files live in the Design Review / Pre-Press / Production sidebars.
const TABS_FULL = ["overview", "boq", "site"];
const TABS_LIMITED = ["overview", "boq"];
const TABS_SITE = ["overview", "boq", "site"];
const TABS_PREPRESS = ["overview", "boq", "site"];
const TABS_DELIVERY_NO_POS = ["overview", "boq", "site"];   // psm/ism
const TABS_CSM = ["overview", "boq", "site"];
const TABS_ACCOUNTS = ["overview", "boq", "site"];

function tabsFor(role) {
  if (role === "admin" || role === "management") return TABS_FULL;
  // Msg 894 — CSM is operational-scope only: no POs (commercial) tab.
  if (role === "csm") return TABS_DELIVERY_NO_POS;
  // Msg 1065 — PSM and ISM lose the POs tab; PO file is none-of-their-business.
  if (role === "psm" || role === "ism") return TABS_DELIVERY_NO_POS;
  if (role === "accounts") return TABS_ACCOUNTS;
  if (role === "prepress") return TABS_PREPRESS;
  if (role === "site_inspector") return ["overview", "boq"];   // on-site overview — view only
  if (["designer", "production"].includes(role)) return TABS_SITE;
  return TABS_LIMITED;
}

export default function ProjectDetail() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const [data, setData] = useState(null);
  const _initTab = params.get("tab");
  const [tab, setTab] = useState((_initTab && !["pos", "files"].includes(_initTab)) ? _initTab : "boq");
  const [users, setUsers] = useState([]);
  const [me, setMe] = useState(null);
  const [archiveJob, setArchiveJob] = useState(null); // {status:'building'|'ready'|'failed'}
  const navigate = useNavigate();

  function load() {
    api.get(`/projects/${code}`)
      .then((r) => setData(r.data))
      .catch((e) => {
        const status = e.response?.status;
        if (status === 403) {
          toast.error("You don't have access to this project.");
          navigate("/projects");
        } else if (status === 404) {
          toast.error("Project not found.");
          navigate("/projects");
        } else {
          toast.error(fmtErr(e.response?.data?.detail) || "Failed to load project.");
        }
      });
  }
  useEffect(() => {
    load();
    api.get("/users").then((r) => setUsers(r.data));
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  }, [code]);

  async function deleteThisProject() {
    const confirm = window.prompt(
      `This permanently deletes project ${code} and ALL its data (BOQ, designs, challans, invoices, photos). This cannot be undone.\n\nType the project code to confirm:`);
    if (confirm == null) return;
    if (confirm.trim() !== code) { toast.error("Project code did not match — deletion cancelled."); return; }
    try {
      await api.delete(`/projects/${code}`);
      toast.success(`Project ${code} deleted`);
      navigate("/projects");
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Failed to delete project");
    }
  }

  if (!data) return <div className="p-8 text-zinc-500">Loading…</div>;
  const p = data.project;
  const isDraft = (p.lifecycle_state || "active") === "draft";

  async function setStage(field, value) {
    try {
      await api.patch(`/projects/${code}/stage`, { [field]: value });
      toast.success("Updated");
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  async function downloadProject(projectCode) {
    if (archiveJob?.status === "building") return;
    try {
      setArchiveJob({ status: "building" });
      const { data } = await api.post(`/projects/${projectCode}/download/prepare`);
      toast.success("Building your archive — large projects can take a few minutes. It will download automatically.");
      const jobId = data.job_id;
      const poll = async () => {
        try {
          const { data: st } = await api.get(`/projects/${projectCode}/download/jobs/${jobId}`);
          if (st.status === "ready") {
            setArchiveJob(null);
            const full = st.url.startsWith("http") ? st.url : `${API.replace(/\/api$/, "")}${st.url}`;
            const a = document.createElement("a");
            a.href = full; document.body.appendChild(a); a.click(); a.remove();
            toast.success("Archive ready — downloading now.");
          } else if (st.status === "failed") {
            setArchiveJob(null);
            toast.error(st.error || "Archive build failed — please try again.");
          } else {
            setTimeout(poll, 4000);
          }
        } catch {
          setTimeout(poll, 6000);
        }
      };
      setTimeout(poll, 4000);
    } catch (e) {
      setArchiveJob(null);
      toast.error(fmtErr(e.response?.data?.detail) || "Download failed");
    }
  }

  async function downloadStatus(projectCode) {
    try {
      toast.success("Preparing the status report…");
      const res = await api.get(`/projects/${projectCode}/status-report`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${projectCode}-status.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Download failed");
    }
  }

  async function downloadClientProgress(projectCode) {
    try {
      toast.success("Preparing the client progress sheet…");
      const res = await api.get(`/projects/${projectCode}/client-progress`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${projectCode}-client-progress.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Download failed");
    }
  }

  async function downloadClientFloorplan(projectCode) {
    try {
      toast.success("Preparing the client floorplan…");
      const res = await api.get(`/projects/${projectCode}/client-floorplan`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${projectCode}-client-floorplan.html`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "No floorplan available yet");
    }
  }

  async function activate() {
    if (!window.confirm("Approve this BOQ and activate the project? It will be locked and move to PROJECTS.")) return;
    try {
      await api.post(`/projects/${code}/activate`);
      toast.success("Project activated");
      load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  return (
    <div className="p-6 lg:p-10" data-testid="project-detail">
      <Link to={isDraft ? "/drafts" : "/projects"} className="inline-flex items-center gap-2 text-sm text-zinc-600 mb-4">
        <ArrowLeft className="w-4 h-4" /> Back to {isDraft ? "drafts" : "projects"}
      </Link>
      <PageHeader kicker={p.utr_no ? `${p.utr_no} · ${p.project_code}` : p.project_code} title={p.name}>
        {isDraft ? (
          <>
            <span className="text-xs font-mono uppercase px-2 py-1 bg-amber-50 border border-amber-300 text-amber-700" data-testid="draft-badge">
              DRAFT · BOQ in build
            </span>
            <Btn onClick={activate} className="bg-emerald-700 hover:bg-emerald-800" data-testid="activate-btn">
              Approve &amp; Activate →
            </Btn>
          </>
        ) : (
          <span className="text-xs font-mono uppercase px-2 py-1 bg-blue-50 border border-blue-200 text-blue-700">{p.overall_status}</span>
        )}
        {/* Phase 34 r7 (Msg 776) — Download Archive + Efficiency Report
            are ADMIN-ONLY. Other roles don't even see the buttons. */}
        {me?.role === "admin" && (
          <>
            <Btn variant="secondary" onClick={() => downloadProject(code)} disabled={archiveJob?.status === "building"} data-testid="download-project-btn">
              <Download className="w-4 h-4" /> {archiveJob?.status === "building" ? "Building archive…" : "Download Archive"}
            </Btn>
            {!isDraft && (
              <Btn variant="secondary" onClick={() => downloadClientFloorplan(code)} data-testid="download-client-floorplan-btn">
                <Download className="w-4 h-4" /> Client Floorplan
              </Btn>
            )}
            {!isDraft && (
              <Link to={`/projects/${code}/report`}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-gradient-to-r from-zinc-900 to-zinc-700 text-white text-sm font-medium shadow-md hover:shadow-lg transition-all"
                    data-testid="project-report-btn">
                <BarChart3 className="w-4 h-4" /> Efficiency Report
              </Link>
            )}
            <Btn variant="danger" onClick={deleteThisProject} data-testid="delete-project-btn">
              <Trash2 className="w-4 h-4" /> Delete Project
            </Btn>
          </>
        )}
        {/* Msg 939 — on-demand status report for the working team (no commercials). */}
        {!isDraft && ["admin", "management", "scope_user", "csm", "psm", "ism"].includes(me?.role) && (
          <Btn variant="secondary" onClick={() => downloadStatus(code)} data-testid="download-status-btn">
            <Download className="w-4 h-4" /> Download Status
          </Btn>
        )}
        {!isDraft && ["admin", "management", "scope_user", "csm", "psm"].includes(me?.role) && (
          <Btn variant="secondary" onClick={() => downloadClientProgress(code)} data-testid="download-client-progress-btn">
            <Download className="w-4 h-4" /> Client Progress
          </Btn>
        )}
      </PageHeader>

      <UtrBar code={code} utr={p.utr_no || ""} me={me} onSaved={load} />

      {isDraft && (
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 text-sm">
          <div className="font-semibold mb-0.5">This is a Project Draft</div>
          <div className="text-zinc-600">Build the Bill of Quantities below. When ready, click <span className="font-semibold">"Approve &amp; Activate"</span> to lock the BOQ and move this into the active <Link to="/projects" className="text-blue-700 hover:underline">Projects</Link> view, where each role will get one-click confirmation per line item.</div>
        </div>
      )}

      {/* Hide overview clutter while in draft mode — focus on BOQ */}
      {!isDraft && (
        <>
          <ClientLoginSection project={p} me={me} onReload={load} />

          <LinkedRecords p={p} data={data} me={me} />

          {/* Msg 1065 — Legacy ClientPOAmendments removed to avoid double
              entries. POs now live exclusively in the dedicated POs tab. */}

          <ClientReviewSection projectCode={code} boq={(data.boq || []).filter((l) => !l.accounts_only)} me={me} onReload={load} />

          <ProjectPipelineChart boq={(data.boq || []).filter((it) => !it.accounts_only)} progressPct={p.progress_pct || 0} />

          {/* Meta — Phase 34 r5 (Msg 760): Pre-Press + Production names
              now visible on the Projects platform header. */}
          <div className="card-flat p-5 mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <Meta k="Client" v={p.client_name} />
            <Meta k="Location" v={p.location || "—"} />
            <Meta k="Deadline" v={p.deadline || "—"} mono />
            <Meta k="BOQ Lines" v={(data.boq || []).length} mono />
            {/* Phase 34 r7 (Msg 776) — Buyer's Order shown ONLY to money
                roles (admin/management/scope_user/accounts). Msg 894 — CSM
                removed (operational-scope only, no commercial). */}
            {["admin", "management", "scope_user", "accounts"].includes(me?.role) && (
              <Meta k="Buyer's Order" v={p.buyer_order_ref ? `${p.buyer_order_ref}${p.buyer_order_type ? " · " + p.buyer_order_type.replace("_", " ") : ""}` : "—"} mono />
            )}
            <Meta k="CSM(s)" v={nameOf(users, p.assignments?.csm)} />
            <Meta k="PSM(s)" v={nameOf(users, p.assignments?.psm)} />
            <Meta k="ISM(s)" v={nameOf(users, p.assignments?.ism)} />
            <Meta k="Designer(s)" v={nameOf(users, p.assignments?.designer)} />
            <Meta k="Pre-Press" v={nameOf(users, p.assignments?.prepress)} />
            <Meta k="Production" v={nameOf(users, p.assignments?.production)} />
          </div>
        </>
      )}

      {isDraft && (
        <div className="card-flat p-5 mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
          <Meta k="Client" v={p.client_name} />
          <Meta k="Location" v={p.location || "—"} />
          <Meta k="Deadline" v={p.deadline || "—"} mono />
          <Meta k="BOQ Lines" v={(data.boq || []).length} mono />
        </div>
      )}

      {/* Tabs — only show in active mode; drafts focus on BOQ */}
      {!isDraft && (
        <div className="flex flex-wrap gap-1 border-b border-zinc-200 mb-4">
          {tabsFor(me?.role).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-4 h-10 text-sm font-medium uppercase tracking-wide ${tab === t ? "bg-zinc-900 text-white" : "hover:bg-zinc-100"}`}
              data-testid={`tab-${t}`}>
              {t.replace("-", " ")}
            </button>
          ))}
        </div>
      )}

      {/* Content: drafts always show BOQ; active shows tabbed content */}
      {isDraft ? (
        <BOQ code={code} items={data.boq} reload={load} project={data.project} me={me} users={users} />
      ) : (
        <>
          {tab === "overview" && <Overview p={p} data={data} users={users} reload={load} me={me} />}
          {tab === "boq" && <BOQ code={code} items={data.boq} reload={load} project={data.project} me={me} users={users} />}
          {tab === "site" && <SiteMeasurementsTab code={code} items={data.boq} project={data.project} me={me} reload={load} />}
          {tab === "consumption" && (me?.role === "admin" || me?.role === "management") && <Consumption items={data.consumption} alloc={data.inventory_allocated} />}
        </>
      )}
    </div>
  );
}

function Financials({ code }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${code}/financials`).then((r) => setData(r.data)); }, [code]);
  if (!data) return <div className="text-zinc-500">Loading…</div>;
  const t = data.totals;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <FStat label="PO Value" v={`₹${data.po_value.toLocaleString()}`} />
        <FStat label="Invoiced" v={`₹${t.invoiced.toLocaleString()}`} accent="text-blue-600" />
        <FStat label="Received" v={`₹${t.received.toLocaleString()}`} accent="text-emerald-600" />
        <FStat label="Pending Receivable" v={`₹${t.pending_receivable.toLocaleString()}`} accent="text-amber-600" />
        <FStat label="Pending Billing" v={`₹${t.pending_billing.toLocaleString()}`} />
        <FStat label="Overdue" v={`₹${t.overdue_amount.toLocaleString()}`} accent="text-rose-600" />
        <FStat label="Supplier Cost" v={`₹${t.supplier_cost.toLocaleString()}`} />
        <FStat label="Margin" v={`₹${t.estimated_margin.toLocaleString()} (${t.margin_pct}%)`} accent={t.estimated_margin > 0 ? "text-emerald-600" : "text-rose-600"} />
      </div>
      <div className="card-flat overflow-x-auto">
        <div className="px-5 py-4 border-b border-zinc-200"><h3 className="font-display font-bold tracking-tight">Invoices</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Invoice</th><th className="text-left p-3">Date</th><th className="text-left p-3">Milestone</th><th className="text-right p-3">Total</th><th className="text-right p-3">Received</th><th className="text-right p-3">Balance</th><th className="text-left p-3">Status</th></tr>
          </thead>
          <tbody>
            {data.invoices.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-zinc-400">No invoices yet</td></tr>}
            {data.invoices.map((i) => (
              <tr key={i.id} className="border-t border-zinc-100">
                <td className="p-3 font-mono text-xs"><Link to={`/invoices/${i.id}`} className="text-blue-600 hover:underline">{i.invoice_number}</Link></td>
                <td className="p-3 font-mono text-xs">{i.invoice_date}</td>
                <td className="p-3 uppercase text-xs">{i.milestone}</td>
                <td className="p-3 text-right font-mono">₹{i.total.toLocaleString()}</td>
                <td className="p-3 text-right font-mono">₹{i.amount_received.toLocaleString()}</td>
                <td className="p-3 text-right font-mono">₹{i.balance.toLocaleString()}</td>
                <td className="p-3 uppercase text-xs">{i.payment_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FStat({ label, v, accent }) {
  return (
    <div className="card-flat p-4">
      <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1 font-display text-xl font-bold ${accent || ""}`}>{v}</div>
    </div>
  );
}

// ───────── Lifecycle Timeline ─────────
function Lifecycle({ p }) {
  const today = new Date().toISOString().slice(0, 10);
  const steps = [
    { key: "po", label: "PO Received", done: !!p.client_po_id || !!p.po_value },
    { key: "advance", label: "Advance Invoiced", done: ["advance_raised", "final_raised", "closed"].includes(p.financial_status) },
    { key: "design", label: "Design Approved", done: p.design_status === "approved" },
    { key: "prodfile", label: "Production File", done: p.production_file_status === "completed" },
    { key: "production", label: "Production Done", done: ["completed", "ready"].includes(p.production_status) },
    { key: "install", label: "Installation Done", done: ["completed", "signoff_done"].includes(p.installation_status) },
    { key: "final", label: "Final Invoice", done: p.financial_status === "final_raised" || p.financial_status === "closed" },
    { key: "closed", label: "Financially Closed", done: p.financial_status === "closed" },
  ];
  const completedCount = steps.filter((s) => s.done).length;
  const pct = Math.round((completedCount / steps.length) * 100);

  return (
    <div className="card-flat p-5 mb-4">
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Project Lifecycle</div>
          <div className="font-display text-lg font-bold tracking-tight">{completedCount} of {steps.length} stages complete · {pct}%</div>
        </div>
        <div className="text-xs text-zinc-500 font-mono">Today {today}</div>
      </div>
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        {steps.map((s, i) => (
          <div key={s.key} className="flex items-center flex-shrink-0">
            <div className={`flex flex-col items-center min-w-[110px] px-2`}>
              <div className={`h-9 w-9 rounded-full flex items-center justify-center text-sm font-bold ${
                s.done ? "bg-emerald-600 text-white" : "bg-zinc-200 text-zinc-500"
              }`}>{s.done ? "✓" : i + 1}</div>
              <div className={`mt-2 text-[11px] font-mono uppercase tracking-wider text-center ${s.done ? "text-emerald-700" : "text-zinc-500"}`}>
                {s.label}
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 w-8 ${steps[i + 1].done && s.done ? "bg-emerald-600" : "bg-zinc-200"}`} />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ───────── Linked Records (single tracking number expansion) ─────────
function LinkedRecords({ p, data, me }) {
  const canSeePO = ["admin", "management", "scope_user", "accounts"].includes(me?.role);
  const [extra, setExtra] = useState({ supplier_pos: [], invoices: [], challans: [], line_pos: [] });
  useEffect(() => {
    api.get("/supplier-pos").then((r) => {
      const filtered = (r.data || []).filter((s) => s.project_code === p.project_code);
      setExtra((e) => ({ ...e, supplier_pos: filtered }));
    }).catch(() => {});
    api.get("/invoices", { params: { project_code: p.project_code } }).then((r) => {
      setExtra((e) => ({ ...e, invoices: r.data || [] }));
    }).catch(() => {});
    api.get(`/projects/${p.project_code}/challans`).then((r) => {
      setExtra((e) => ({ ...e, challans: r.data || [] }));
    }).catch(() => {});
    // Msg 1066 — pull the line-PO list (Single-Track now reflects this count
    // instead of the legacy project-level client_po flag).
    if (canSeePO) {
      api.get(`/projects/${p.project_code}/line-pos`).then((r) => {
        setExtra((e) => ({ ...e, line_pos: r.data || [] }));
      }).catch(() => {});
    }
  }, [p.project_code, canSeePO]);

  const inv = data.inventory_allocated || [];
  const totalChallanLines = extra.challans.reduce((a, c) => a + (c.items_count || 0), 0);
  const installedLines = extra.challans.reduce((a, c) => a + (c.installed_count || 0), 0);
  const installPct = totalChallanLines ? Math.round((installedLines / totalChallanLines) * 100) : 0;

  return (
    <div className="card-flat p-5 mb-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Single Track Number</div>
          <div className="font-display font-bold tracking-tight">All records linked to <span className="font-mono text-blue-600">{p.utr_no || p.project_code}</span>{p.utr_no && <span className="font-mono text-xs text-zinc-400 font-normal ml-2">({p.project_code})</span>}</div>
        </div>
        {totalChallanLines > 0 && (
          <div className="text-right">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Site Installation</div>
            <div className={`font-display font-bold ${installPct === 100 ? "text-emerald-600" : "text-zinc-900"}`}
                 data-testid="overview-install-pct">
              {installedLines} / {totalChallanLines} ({installPct}%)
            </div>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
        {/* Msg 1066 — Coverage chip: shows how many BOQ lines have a PO/email
            on file out of total. Only visible to roles allowed to see POs. */}
        {canSeePO && (() => {
          const total = (data.boq || []).length;
          const linked = new Set();
          for (const po of (extra.line_pos || [])) {
            for (const b of (po.boq_ids || [])) linked.add(b);
          }
          const covered = (data.boq || []).filter((b) => linked.has(b.id)).length;
          const value = total
            ? `${covered}/${total} covered`
            : "no lines yet";
          return (
            <LinkChip label="PO Coverage"
                      value={value}
                      link={`/projects/${p.project_code}?tab=pos`}
                      count={extra.line_pos.length} />
          );
        })()}
        <LinkChip label="Challans" value={`${extra.challans.length} issued`}
          link={extra.challans.length > 0 ? `/projects/${p.project_code}?tab=files` : null}
          count={extra.challans.length} />
        <LinkChip label="Team Members" value={`${Object.values(p.assignments || {}).flat().filter(Boolean).length} assigned`}
          link={null} count={1} />
      </div>
    </div>
  );
}

function LinkChip({ label, value, link, count }) {
  const Wrapper = link ? Link : "div";
  return (
    <Wrapper to={link} className={`block border p-3 ${count > 0 ? "border-blue-200 bg-blue-50" : "border-zinc-200 bg-zinc-50"} ${link ? "hover:border-blue-400 transition-colors" : ""}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className={`mt-1 font-mono text-sm font-semibold ${count > 0 ? "text-blue-700" : "text-zinc-500"}`}>{value}</div>
    </Wrapper>
  );
}

function nameOf(users, idOrIds) {
  if (!idOrIds) return "—";
  if (Array.isArray(idOrIds)) {
    if (idOrIds.length === 0) return "—";
    return idOrIds.map((id) => {
      const u = users.find((x) => x.id === id);
      return u ? u.name : "—";
    }).join(", ");
  }
  const u = users.find((x) => x.id === idOrIds);
  return u ? u.name : "—";
}

function Meta({ k, v, mono }) {
  return (
    <div>
      <div className="text-xs font-mono uppercase tracking-wider text-zinc-500">{k}</div>
      <div className={`mt-0.5 ${mono ? "font-mono" : ""}`}>{v}</div>
    </div>
  );
}

// Composite stage helpers — collapse 8 raw stages into the 6 user-facing pipeline stages.
// Prepress = pre_press + sample_mockup + files_received (all owned by production_file).
// DC = delivery_progress (auto-DC fires when delivery completes).
// 'na' counts as done (the stage is explicitly skipped for this line).
const PIPELINE_DONE = (v) => v === "completed" || v === "done" || v === "na";
const PIPELINE_INPROG = (v) => v === "in_progress";
const PIPELINE_NA = (v) => v === "na";

function deriveSixStages(it) {
  const it_ = it || {};
  // Pre-Press = the pre-press / sample preparation step. "Files Ready" is split
  // out as its own stage — the final production file sitting in the production
  // user's account, ready to print/produce. (Msg 936)
  const sub = [it_.pre_press, it_.sample_mockup];
  const allDone = sub.every(PIPELINE_DONE);
  const anyTouched = sub.some((v) => PIPELINE_DONE(v) || PIPELINE_INPROG(v));
  const allNa = sub.every(PIPELINE_NA);
  const prepressVal = allNa ? "na" : (allDone ? "completed" : (anyTouched ? "in_progress" : "pending"));
  // Design → Client OK → Site Measure → Pre-Press → Files Ready → Production → Delivery → Installation
  return [
    { key: "design", label: "Design", v: it_.design_approval },
    { key: "client_approval", label: "Client OK", v: it_.client_design_approval || "na" },
    { key: "site", label: "Site Measure", v: it_.site_audit || "pending" },
    { key: "prepress", label: "Pre-Press", v: prepressVal },
    { key: "files", label: "Files Ready", v: it_.files_received || "pending" },
    { key: "production", label: "Production", v: it_.production_progress },
    { key: "delivery", label: "Delivery", v: it_.delivery_progress },
    { key: "installation", label: "Installation", v: it_.installation_progress },
  ];
}

// Outsourced lines have a SHORTER, dynamic pipeline that ends at the Final
// Production File. The internal Production / Delivery / Installation / DC stages
// are NOT shown at all (per spec — not even as "N/A").
function derivePipelineStages(it) {
  const it_ = it || {};
  // A variation's journey ends at the production file (never delivery /
  // installation). Each earlier stage can be skipped by the CSM.
  if (it_.is_variation) {
    const stages = [];
    if (!it_.no_designer) {
      stages.push({ key: "design", label: "Design", v: it_.design_approval });
      stages.push({ key: "client_approval", label: "Approval", v: it_.client_design_approval || "na" });
    }
    if (!it_.no_site_measure) stages.push({ key: "site", label: "Site Measure", v: it_.site_audit || "pending" });
    if (!it_.no_prepress) {
      const sub = [it_.pre_press, it_.sample_mockup, it_.files_received];
      const allDone = sub.every(PIPELINE_DONE);
      const anyTouched = sub.some((v) => PIPELINE_DONE(v) || PIPELINE_INPROG(v));
      stages.push({ key: "prepress", label: "Pre-Press", v: allDone ? "completed" : (anyTouched ? "in_progress" : "pending") });
    }
    if (!it_.no_production) {
      const pf = it_.production_file_status;
      const finalVal = pf === "approved" ? "completed"
        : (["modify", "rejected"].includes(pf) ? "in_progress"
          : (it_.production_file_url ? "in_progress" : "pending"));
      stages.push({ key: "production_file", label: "Production File", v: finalVal });
    }
    if (it_.installation_progress === "completed")
      stages.push({ key: "installation", label: "Installed", v: "completed" });
    if (!stages.length) stages.push({ key: "design", label: "Design", v: it_.design_approval });
    return stages;
  }
  if (it_.production_type === "outsourced") {
    const sub = [it_.pre_press, it_.sample_mockup, it_.files_received];
    const allDone = sub.every(PIPELINE_DONE);
    const anyTouched = sub.some((v) => PIPELINE_DONE(v) || PIPELINE_INPROG(v));
    const prepressVal = allDone ? "completed" : (anyTouched ? "in_progress" : "pending");
    const pf = it_.production_file_status;
    const finalVal = pf === "approved" ? "completed"
      : (["modify", "rejected"].includes(pf) ? "in_progress"
        : (it_.production_file_url ? "in_progress" : "pending"));
    const stages = [
      { key: "design", label: "Design", v: it_.design_approval },
      { key: "client_approval", label: "Approval", v: it_.client_design_approval || "na" },
      { key: "site", label: "Site Measure", v: it_.site_audit || "pending" },
      { key: "prepress", label: "Pre-Press", v: prepressVal },
      { key: "production_file", label: "Final File", v: finalVal },
    ];
    // Once the Site Inspector submits the final installed photo, the outsourced
    // wall is explicitly Installed & complete.
    if (it_.installation_progress === "completed")
      stages.push({ key: "installation", label: "Installed", v: "completed" });
    return stages;
  }
  return deriveSixStages(it);
}

// Aggregate stage-completion pipeline chart for a project's BOQ lines (active mode).
// Shows the same 6 named stages used in the per-row strip, so numbers always align.
function ProjectPipelineChart({ boq, progressPct }) {
  const total = (boq || []).length;
  return (
    <div className="card-flat p-5 mb-6" data-testid="project-pipeline-chart">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-5">
        <div>
          <div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Project Pipeline</div>
          <div className="font-display text-xl font-bold tracking-tight mt-0.5">
            {progressPct}% <span className="text-zinc-400 font-normal text-sm">overall across {total} BOQ {total === 1 ? "line" : "lines"}</span>
          </div>
        </div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500" />Completed</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-400" />In-Progress</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-zinc-200" />Pending</span>
        </div>
      </div>
      {total === 0 ? (
        <div className="text-zinc-400 text-sm py-6 text-center">No BOQ lines yet.</div>
      ) : (
        <div className="space-y-3">
          {deriveSixStages({}).map((stage) => {
            // Production / Delivery / Installation only apply to IN-HOUSE lines.
            // Compute those rows over in-house lines only, and hide the row
            // entirely when every line is outsourced.
            const internalOnly = ["production", "delivery", "installation"].includes(stage.key);
            const applicable = internalOnly
              ? (boq || []).filter((it) => it.production_type !== "outsourced")
              : (boq || []);
            const denom = applicable.length;
            if (internalOnly && denom === 0) return null;
            const done = applicable.filter((it) => PIPELINE_DONE(deriveSixStages(it).find((s) => s.key === stage.key)?.v)).length;
            const inProg = applicable.filter((it) => PIPELINE_INPROG(deriveSixStages(it).find((s) => s.key === stage.key)?.v)).length;
            const donePct = denom ? (done / denom) * 100 : 0;
            const progPct = denom ? (inProg / denom) * 100 : 0;
            return (
              <div key={stage.key} className="grid grid-cols-[110px_1fr_90px] items-center gap-4"
                   data-testid={`pipeline-stage-${stage.key}`}>
                <div className="text-[11px] font-mono uppercase tracking-widest font-semibold text-zinc-700">{stage.label}</div>
                <div className="h-5 bg-zinc-100 relative overflow-hidden">
                  <div className="absolute inset-y-0 left-0 bg-emerald-500 transition-all duration-500"
                       style={{ width: `${donePct}%` }} />
                  <div className="absolute inset-y-0 bg-amber-300/80 transition-all duration-500"
                       style={{ left: `${donePct}%`, width: `${progPct}%` }} />
                </div>
                <div className="text-xs font-mono text-right whitespace-nowrap">
                  <span className="font-bold">{done}</span>
                  <span className="text-zinc-400">/{denom}</span>
                  <span className="ml-2 text-[10px] text-emerald-700">{Math.round(donePct)}%</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function StageCard({ label, value, options, onChange, testid }) {
  // Legacy project-level status dropdown — deprecated in favour of per-BOQ-line PipelineStrip.
  return null;
}

function Overview({ p, data, users, reload, me }) {
  const asn = p.assignments || {};
  const inSlot = (slot) => {
    const v = asn[slot]; const arr = Array.isArray(v) ? v : (v ? [v] : []);
    return arr.includes(me?.id);
  };
  const isAdminMgmt = me && (me.role === "admin" || me.role === "management");
  const canManagers = isAdminMgmt || me?.role === "scope_user";
  const canDesign = isAdminMgmt || (me?.role === "csm" && inSlot("csm"));
  const canPrepress = isAdminMgmt || (me?.role === "psm" && inSlot("psm"));
  const canSiteInspector = isAdminMgmt || (me?.role === "ism" && inSlot("ism"));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="space-y-4">
        <TeamSection title="Project Managers & Oversight" tid="managers" code={p.project_code}
          assignments={asn} users={users} canEdit={canManagers} reload={reload}
          hint="Set by the Scope User"
          slots={[
            { key: "csm", label: "CSM (Client Success Manager)", role: "csm" },
            { key: "psm", label: "PSM (Project Site Manager)", role: "psm" },
            { key: "ism", label: "ISM (Implementation — visibility)", role: "ism" },
            { key: "management", label: "Management (oversight)", role: "management" },
          ]} />
        <TeamSection title="Design Team" tid="design" code={p.project_code}
          assignments={asn} users={users} canEdit={canDesign} reload={reload}
          hint="Managed by the CSM"
          slots={[{ key: "designer", label: "Designer(s)", role: "designer" }]} />
        <TeamSection title="Pre-Press Team" tid="prepress" code={p.project_code}
          assignments={asn} users={users} canEdit={canPrepress} reload={reload}
          hint="Managed by the PSM (after designs are approved)"
          slots={[{ key: "prepress", label: "Pre-Press", role: "prepress" }]} />
        <TeamSection title="Site Pre-Audit Team" tid="preaudit" code={p.project_code}
          assignments={asn} users={users} canEdit={canPrepress} reload={reload}
          hint="Managed by the PSM — takes the on-site measurements"
          slots={[{ key: "preaudit", label: "Pre-Audit (site measurements)", role: "pre_audit" }]} />
        <TeamSection title="Production Team" tid="production" code={p.project_code}
          assignments={asn} users={users} canEdit={canPrepress} reload={reload}
          hint="Managed by the PSM — receives the final production file to download"
          slots={[{ key: "production", label: "Production (downloads final file)", role: "production" }]} />
        <TeamSection title="Site Inspector" tid="site_inspector" code={p.project_code}
          assignments={asn} users={users} canEdit={canSiteInspector} reload={reload}
          hint="Assigned by the ISM — on-site overview (view-only: BOQ + Floorplan)"
          slots={[{ key: "site_inspector", label: "Site Inspector (view-only)", role: "site_inspector" }]} />
      </div>
      <ProjectDetailsCard p={p} me={me} reload={reload} />
    </div>
  );
}

function ProjectDetailsCard({ p, me, reload }) {
  const canEdit = ["admin", "management", "scope_user"].includes(me?.role);
  const [edit, setEdit] = useState(false);
  const [desc, setDesc] = useState(p.description || "");
  const [req, setReq] = useState(p.requirements || "");
  const [saving, setSaving] = useState(false);
  async function save() {
    setSaving(true);
    try {
      await api.patch(`/projects/${p.project_code}/details`, { description: desc, requirements: req });
      toast.success("Project details updated");
      setEdit(false); reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  return (
    <div className="card-flat p-5 space-y-3 self-start" data-testid="project-details-card">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold tracking-tight">Project Details</h3>
        {canEdit && !edit && (
          <Btn variant="secondary" onClick={() => { setDesc(p.description || ""); setReq(p.requirements || ""); setEdit(true); }}
               data-testid="project-details-edit">Edit</Btn>
        )}
      </div>
      {edit ? (
        <div className="space-y-3">
          <Field label="Description">
            <textarea rows="3" value={desc} onChange={(e) => setDesc(e.target.value)}
                      className="w-full p-3 border border-zinc-300 text-sm" data-testid="project-details-desc" />
          </Field>
          <Field label="Requirements">
            <textarea rows="3" value={req} onChange={(e) => setReq(e.target.value)}
                      className="w-full p-3 border border-zinc-300 text-sm" data-testid="project-details-req" />
          </Field>
          <div className="flex gap-2">
            <Btn onClick={save} disabled={saving} data-testid="project-details-save">{saving ? "Saving…" : "Save"}</Btn>
            <Btn variant="secondary" onClick={() => setEdit(false)}>Cancel</Btn>
          </div>
        </div>
      ) : (
        <>
          <Meta k="Description" v={p.description || "—"} />
          <Meta k="Requirements" v={p.requirements || "—"} />
        </>
      )}
    </div>
  );
}

function TeamSection({ title, tid, slots, code, assignments, users, canEdit, reload, hint }) {
  const [edit, setEdit] = useState(false);
  const [a, setA] = useState({});
  function startEdit() {
    const init = {}; slots.forEach((s) => { init[s.key] = assignments?.[s.key] || []; });
    setA(init); setEdit(true);
  }
  async function save() {
    try {
      const payload = {}; slots.forEach((s) => { payload[s.key] = a[s.key] || []; });
      await api.patch(`/projects/${code}/assignments`, payload);
      toast.success("Team updated"); setEdit(false); reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  return (
    <div className="card-flat p-5 space-y-3" data-testid={`team-section-${tid}`}>
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display font-bold tracking-tight">{title}</h3>
          {hint && <div className="text-[11px] text-zinc-400 font-mono uppercase tracking-widest mt-0.5">{hint}</div>}
        </div>
        {canEdit && !edit && <Btn variant="secondary" onClick={startEdit} data-testid={`edit-team-${tid}`}>Edit</Btn>}
      </div>
      {edit && canEdit ? (
        <>
          {slots.map((s) => (
            <UserPick key={s.key} label={s.label} role={s.role} users={users}
                      value={a[s.key]} onChange={(v) => setA({ ...a, [s.key]: v })} />
          ))}
          <div className="flex gap-2 pt-2">
            <Btn onClick={save} data-testid={`save-team-${tid}`}>Save</Btn>
            <Btn variant="secondary" onClick={() => setEdit(false)}>Cancel</Btn>
          </div>
        </>
      ) : (
        slots.map((s) => <Meta key={s.key} k={s.label} v={nameOf(users, assignments?.[s.key])} />)
      )}
    </div>
  );
}

function UserPick({ label, role, users, value, onChange }) {
  const list = users.filter((u) => u.role === role || u.role === "admin");
  const selected = Array.isArray(value) ? value : (value ? [value] : []);
  function toggle(uid) {
    const next = selected.includes(uid) ? selected.filter((x) => x !== uid) : [...selected, uid];
    onChange(next);
  }
  return (
    <Field label={label}>
      <div className="border border-zinc-300 max-h-32 overflow-y-auto bg-white">
        {list.length === 0 && <div className="px-3 py-2 text-xs text-zinc-400">No users with this role</div>}
        {list.map((u) => (
          <label key={u.id} className="flex items-center gap-2 px-3 h-9 text-sm hover:bg-zinc-50 cursor-pointer">
            <input type="checkbox" checked={selected.includes(u.id)} onChange={() => toggle(u.id)} />
            <span className="flex-1">{u.name}</span>
            <span className="text-xs text-zinc-500 font-mono">{u.email}</span>
          </label>
        ))}
      </div>
      {selected.length > 0 && <div className="text-xs text-zinc-500 mt-1">{selected.length} selected</div>}
    </Field>
  );
}

function Designs({ code, designs, reload }) {
  const [v, setV] = useState({ version: "v1", file_url: "", notes: "", status: "pending" });
  async function add(e) {
    e.preventDefault();
    try { await api.post(`/projects/${code}/designs`, v); toast.success("Added"); reload();
      setV({ version: "v" + (designs.length + 2), file_url: "", notes: "", status: "pending" });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="card-flat p-4 grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
        <Field label="Version"><Input value={v.version} onChange={(e) => setV({ ...v, version: e.target.value })} required /></Field>
        <Field label="File URL"><Input value={v.file_url} onChange={(e) => setV({ ...v, file_url: e.target.value })} placeholder="https://..." /></Field>
        <Field label="Status">
          <Select value={v.status} onChange={(e) => setV({ ...v, status: e.target.value })}>
            {["pending", "sent", "modify", "rejected", "approved"].map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Notes"><Input value={v.notes} onChange={(e) => setV({ ...v, notes: e.target.value })} /></Field>
        <Btn type="submit" data-testid="add-design"><Plus className="w-4 h-4" /> Add</Btn>
      </form>
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Version</th><th className="text-left p-3">Status</th><th className="text-left p-3">File</th><th className="text-left p-3">Notes</th><th className="text-left p-3">By</th><th className="text-left p-3">Date</th></tr>
          </thead>
          <tbody>
            {designs.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-zinc-400">No designs yet</td></tr>}
            {designs.map((d) => (
              <tr key={d.id} className="border-t border-zinc-100">
                <td className="p-3 font-mono">{d.version}</td>
                <td className="p-3 uppercase font-mono text-xs">{d.status}</td>
                <td className="p-3">{d.file_url ? <a href={d.file_url} target="_blank" rel="noreferrer" className="text-blue-600 underline text-xs">Open</a> : "—"}</td>
                <td className="p-3 text-xs">{d.notes}</td>
                <td className="p-3 text-xs">{d.created_by_name}</td>
                <td className="p-3 text-xs font-mono">{(d.created_at || "").slice(0, 10)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PFile({ code, pf, reload }) {
  const [f, setF] = useState({ file_url: pf?.file_url || "", notes: pf?.notes || "", status: pf?.status || "pending" });
  async function save(e) {
    e.preventDefault();
    try { await api.post(`/projects/${code}/production-file`, f); toast.success("Saved"); reload(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  return (
    <form onSubmit={save} className="card-flat p-5 max-w-2xl space-y-4">
      <h3 className="font-display font-bold tracking-tight">Production File</h3>
      <Field label="File URL"><Input value={f.file_url} onChange={(e) => setF({ ...f, file_url: e.target.value })} placeholder="Drive / Dropbox link" /></Field>
      <Field label="Notes"><textarea rows="3" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className="w-full p-3 border border-zinc-300 text-sm" /></Field>
      <Field label="Status"><Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
        <option value="pending">Pending</option><option value="in_progress">In Progress</option><option value="completed">Completed</option>
      </Select></Field>
      <Btn type="submit" data-testid="save-pf">Save Production File</Btn>
    </form>
  );
}

// Traffic-light status badge
function StatusLight({ status }) {
  const map = {
    not_started: { c: "bg-rose-500", label: "Not Started" },
    in_progress: { c: "bg-amber-500", label: "In Progress" },
    completed: { c: "bg-emerald-500", label: "Completed" },
    pending: { c: "bg-rose-500", label: "Pending" },
    partial: { c: "bg-amber-500", label: "Partial" },
    ready: { c: "bg-emerald-500", label: "Ready" },
  };
  const s = map[status] || { c: "bg-zinc-400", label: status };
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={`w-2.5 h-2.5 rounded-full ${s.c}`} />
      <span className="font-mono uppercase tracking-wider">{s.label}</span>
    </span>
  );
}

// Per-role stage ownership — each user sees ONLY the stages they own.
// Phase 34 r4 + r6 + Msg 1062 — ALL stages now auto-complete on the relevant
// upload/action. There are NO manual "Mark Done" buttons left:
//   • designer.design_approval         → auto on prepress-accept (r5)
//   • psm.site_audit / csm.site_audit  → auto on site-measurement save (r4)
//   • prepress.sample_mockup/pre_press/files_received → auto on production-file upload (r4)
//   • production.production_progress   → auto on QC document upload (Msg 1062)
//   • ism.delivery_progress            → auto on signed-DC upload (r6 / Msg 768)
//   • ism.installation_progress moved to PSM and auto on install-photo (r5+r6)
const ROLE_ACTIONS = {
  designer:   { stages: [] },
  csm:        { stages: [] },
  psm:        { stages: [] },
  prepress:   { stages: [] },
  ism:        { stages: [] },
  production: { stages: [] },
};

const ROLE_SLOT = {
  designer: "designer", csm: "csm", psm: "psm", ism: "ism",
  prepress: "prepress", production: "production",
};

const isCompleted = (v) => v === "completed" || v === "done" || v === "na";
const isNA = (v) => v === "na";

// Compact horizontal pipeline strip used in active-mode BOQ rows.
// 6 named stages: design · prepress · production · delivery · installation · DC.
// Each chip shows its label below; turns emerald when completed, amber when in-progress.
function BoqStageFunnel({ items }) {
  const work = (items || []).filter((it) => !it.is_scope_parent);
  if (work.length === 0) return null;
  const stages = deriveSixStages({});
  return (
    <div className="card-flat p-5 mb-4" data-testid="boq-stage-funnel">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">Where the work stands</div>
        <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-wider text-zinc-400">
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" />Done</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-amber-400" />In progress</span>
          <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-zinc-200" />Pending</span>
        </div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
        {stages.map((stage) => {
          const internalOnly = ["production", "delivery", "installation"].includes(stage.key);
          const applicable = internalOnly ? work.filter((it) => it.production_type !== "outsourced") : work;
          const denom = applicable.length || 1;
          const done = applicable.filter((it) => PIPELINE_DONE(deriveSixStages(it).find((s) => s.key === stage.key)?.v)).length;
          const inProg = applicable.filter((it) => PIPELINE_INPROG(deriveSixStages(it).find((s) => s.key === stage.key)?.v)).length;
          const donePct = Math.round((done / denom) * 100);
          return (
            <div key={stage.key} data-testid={`boq-funnel-${stage.key}`}>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600 font-semibold truncate" title={stage.label}>{stage.label}</div>
              <div className="h-2 bg-zinc-100 rounded-sm overflow-hidden relative mt-1.5">
                <div className="absolute inset-y-0 left-0 bg-emerald-500 transition-all duration-500" style={{ width: `${(done / denom) * 100}%` }} />
                <div className="absolute inset-y-0 bg-amber-400/80 transition-all duration-500" style={{ left: `${(done / denom) * 100}%`, width: `${(inProg / denom) * 100}%` }} />
              </div>
              <div className="text-[10px] font-mono text-zinc-500 mt-1 tabular-nums">
                <span className="font-bold text-zinc-800">{done}</span>/{applicable.length} · {donePct}%
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PipelineStrip({ it, stages: stagesProp }) {
  const stages = stagesProp || derivePipelineStages(it);
  const outsourced = !stagesProp && it?.production_type === "outsourced";
  const total = stages.length;
  const doneAll = stages.filter((s) => PIPELINE_DONE(s.v)).length;
  const pct = total ? Math.round((doneAll / total) * 100) : 0;
  const complete = doneAll === total;
  // "where is it now": first in-progress stage, else first not-done stage.
  const current = stages.find((s) => PIPELINE_INPROG(s.v)) || stages.find((s) => !PIPELINE_DONE(s.v));
  const inProg = current && PIPELINE_INPROG(current.v);
  const chipTone = complete ? "bg-emerald-100 text-emerald-700"
    : inProg ? "bg-amber-100 text-amber-800" : "bg-zinc-100 text-zinc-600";
  const chipDot = complete ? "bg-emerald-500" : inProg ? "bg-amber-400" : "bg-zinc-400";
  const barPct = complete ? "bg-emerald-500" : "bg-zinc-900";

  return (
    <div className="min-w-[240px] max-w-[320px] mx-auto" data-testid={`pipeline-strip-${it.id}`}>
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <span className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full text-[11px] font-medium whitespace-nowrap ${chipTone}`}
              data-testid={`pipeline-current-${it.id}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${chipDot}`} />
          {complete ? "Complete" : (
            <>{current?.label}<span className="opacity-60 font-normal">{inProg ? " · in progress" : " · up next"}</span></>
          )}
          {outsourced && <span className="text-violet-600">· outsourced</span>}
        </span>
        <span className="text-[11px] font-mono font-bold tabular-nums text-zinc-700">{pct}%</span>
      </div>
      {/* Segmented progress with the stage name under each segment */}
      <div className="flex gap-1">
        {stages.map((s) => {
          const na = PIPELINE_NA(s.v);
          const done = PIPELINE_DONE(s.v);
          const ip = PIPELINE_INPROG(s.v);
          const isCurrent = current && current.key === s.key && !complete;
          const cls = na ? "bg-zinc-200 bg-[repeating-linear-gradient(45deg,rgba(0,0,0,0.05)_0,rgba(0,0,0,0.05)_2px,transparent_2px,transparent_4px)]"
            : done ? "bg-emerald-500" : ip ? "bg-amber-400" : "bg-zinc-200";
          return (
            <div key={s.key} className="flex flex-col items-center gap-0.5 flex-1 min-w-0"
                 title={`${s.label}: ${na ? "Not applicable" : (s.v || "pending")}`}
                 data-testid={`pipeline-chip-${it.id}-${s.key}`}>
              <div className={`h-2 w-full rounded-sm transition-colors ${cls} ${isCurrent ? "ring-2 ring-amber-300" : ""}`} />
              <span className={`text-[8px] font-mono uppercase tracking-wider text-center leading-tight truncate w-full ${done ? "text-emerald-600" : ip ? "text-amber-700 font-semibold" : "text-zinc-400"}`}>{s.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Sequential prereqs (mirror of backend PREREQS) — Feb 2026 sequence correction.
// Design → Client OK → Site Measurement → Sample → Pre-Press → Files → Production → Delivery → Install
const STAGE_PREREQS = {
  client_design_approval: ["design_approval"],
  site_audit:             ["client_design_approval"],
  sample_mockup:          ["site_audit"],
  pre_press:              ["site_audit", "sample_mockup"],
  files_received:         ["pre_press"],
  production_progress:    ["files_received"],
  delivery_progress:      ["production_progress"],
  installation_progress:  ["delivery_progress"],
};
const STAGE_LABEL = {
  design_approval: "Design", site_audit: "Site Measurement",
  client_design_approval: "Client Approval",
  pre_press: "Pre-Press", sample_mockup: "Mock Sample", files_received: "Files Ready",
  production_progress: "Production",
  delivery_progress: "Delivery", installation_progress: "Installation",
};

// Msg 875 — derive a brief, line-specific status sentence for each role
// (used in the "My Action" column for roles whose actions are auto-completed
// by upstream file uploads). Returns {label, cls, icon, title}.
function deriveLineStatus(it, role) {
  const dc = it.line_dc || null;
  const has = (v) => v && v !== "pending" && v !== "na";

  // helpers
  const PENDING = "bg-zinc-50 text-zinc-500 border-zinc-200 italic";
  const ACTIVE  = "bg-amber-50 text-amber-800 border-amber-200";
  const DONE    = "bg-emerald-50 text-emerald-700 border-emerald-200";
  const BLOCKED = "bg-rose-50 text-rose-700 border-rose-200";

  if (role === "designer") {
    if (it.client_design_approval === "completed" && it.final_design_file_url
        && it.final_design_status === "accepted") {
      return { label: "✓ Final design accepted", cls: DONE };
    }
    if (it.final_design_status === "rework") {
      return { label: "⟲ Pre-Press requested rework", cls: BLOCKED,
               title: it.final_design_review_note || "" };
    }
    if (it.client_design_approval === "completed" && !it.final_design_file_url) {
      return { label: "Upload final design now", cls: ACTIVE };
    }
    if (it.client_design_approval === "rejected") {
      return { label: "⟲ Client rejected · re-share", cls: BLOCKED };
    }
    if (it.current_design_file_id) {
      return { label: "Awaiting client review", cls: ACTIVE };
    }
    return { label: "Share concept with client", cls: PENDING };
  }

  if (role === "csm") {
    if (it.installation_progress === "completed") {
      return { label: "✓ Installed", cls: DONE };
    }
    if (dc?.signed_pdf_url) {
      return { label: "Delivered · awaiting install", cls: ACTIVE };
    }
    if (dc?.challan_number) {
      return { label: "DC raised · awaiting signed", cls: ACTIVE };
    }
    if (it.client_design_approval === "completed") {
      return { label: "In production pipeline", cls: ACTIVE };
    }
    if (it.current_design_file_id) {
      return { label: "Awaiting client approval", cls: ACTIVE };
    }
    return { label: "Awaiting designer", cls: PENDING };
  }

  if (role === "psm") {
    if (it.installation_progress === "completed") {
      return { label: "✓ Install photos uploaded", cls: DONE };
    }
    if (dc?.signed_pdf_url) {
      return { label: "Upload install photos now", cls: ACTIVE };
    }
    if (it.site_audit === "completed" || it.site_audit === "done") {
      return { label: "✓ Measurements saved", cls: DONE };
    }
    if (it.client_design_approval === "completed") {
      return { label: "Capture site measurements", cls: ACTIVE };
    }
    return { label: "Awaiting client approval", cls: PENDING };
  }

  if (role === "prepress") {
    if (it.production_file_status === "approved") {
      return { label: "✓ Production file approved", cls: DONE };
    }
    if (["modify", "rejected"].includes(it.production_file_status)) {
      return { label: "⟲ Client asked for revisions", cls: BLOCKED,
               title: it.production_file_client_action_note || "" };
    }
    if (it.production_file_url) {
      return { label: "Awaiting client review", cls: ACTIVE };
    }
    if (it.final_design_status === "accepted") {
      return { label: "Upload production file", cls: ACTIVE };
    }
    if (it.final_design_file_url) {
      return { label: "Review designer's final file", cls: ACTIVE };
    }
    return { label: "Awaiting designer's final", cls: PENDING };
  }

  if (role === "production") {
    if (has(it.qc_document_url)) {
      return { label: "✓ QC filed · DC raised", cls: DONE };
    }
    if (it.production_file_status === "approved") {
      return { label: "Upload QC document next", cls: ACTIVE };
    }
    return { label: "Awaiting client approval", cls: PENDING };
  }

  if (role === "ism") {
    if (it.installation_progress === "completed") {
      return { label: "✓ Installed", cls: DONE };
    }
    if (dc?.signed_pdf_url) {
      return { label: "Awaiting install photos (PSM)", cls: PENDING };
    }
    if (dc?.challan_number) {
      return { label: "Upload signed DC now", cls: ACTIVE };
    }
    return { label: "Awaiting production QC", cls: PENDING };
  }

  return { label: "Auto-tracked", cls: PENDING };
}

function MyActionCell({ it, me, project, onAdvance }) {
  if (!me) return <span className="text-zinc-300 text-xs">—</span>;
  if (me.role === "admin") {
    return <span className="text-[10px] font-mono uppercase tracking-wider text-amber-700">admin</span>;
  }
  const cfg = ROLE_ACTIONS[me.role];
  if (!cfg) {
    return <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400">view-only</span>;
  }
  const slot = ROLE_SLOT[me.role];
  const assigned = ((project?.assignments) || {})[slot] || [];
  const assignedArr = Array.isArray(assigned) ? assigned : [assigned];
  if (!assignedArr.includes(me.id)) {
    return <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400" data-testid="not-assigned">not assigned</span>;
  }
  // Phase 34 r4 — roles whose stages are now all auto-completed via file
  // uploads (designer, csm, psm, prepress) have no manual mark-complete
  // buttons. Msg 875 — show a brief, line-specific CURRENT STATUS instead
  // of a generic role cue so the operator instantly knows what's happening.
  if (cfg.stages.length === 0) {
    const status = deriveLineStatus(it, me.role);
    return (
      <span className={`inline-flex items-center justify-center px-2 py-1 text-[10px] font-mono uppercase tracking-wider border ${status.cls}`}
            title={status.title || ""}
            data-testid={`status-cue-${me.role}-${it.id}`}>
        {status.icon} {status.label}
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1">
      {cfg.stages.map((s) => {
        const cur = it[s.key];
        if (isNA(cur)) {
          return (
            <span key={s.key} data-testid={`na-${it.id}-${s.key}`}
              className="inline-flex items-center justify-center px-2 py-1 text-[10px] font-mono uppercase tracking-wider bg-zinc-50 text-zinc-500 border border-zinc-200 italic">
              N/A · skipped
            </span>
          );
        }
        if (isCompleted(cur)) {
          return (
            <span key={s.key}
              className="inline-flex items-center justify-center px-2 py-1 text-[10px] font-mono uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
              ✓ {s.label}
            </span>
          );
        }
        // Sequential prerequisite check
        const prereqs = STAGE_PREREQS[s.key] || [];
        const blocked = prereqs.filter((p) => !isCompleted(it[p]));
        if (blocked.length) {
          const blockingLabel = STAGE_LABEL[blocked[0]] || blocked[0];
          return (
            <span key={s.key}
              data-testid={`locked-${it.id}-${s.key}`}
              title={`Awaiting ${blocked.map((b) => STAGE_LABEL[b]).join(" + ")}`}
              className="inline-flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider bg-zinc-50 text-zinc-400 border border-zinc-200">
              🔒 {s.label} · awaits {blockingLabel}
            </span>
          );
        }
        // Phase 33 — production cannot start until client approves the production file.
        if (s.key === "production_progress" && it.production_file_status !== "approved") {
          const reason = !it.production_file_url
            ? "Pre-Press hasn't uploaded the production file"
            : "Awaiting client approval of the production file";
          return (
            <span key={s.key}
              data-testid={`prod-locked-${it.id}`}
              title={reason}
              className="inline-flex items-center justify-center gap-1 px-2 py-1 text-[10px] font-mono uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200">
              🔒 {s.label} · awaits client approval
            </span>
          );
        }
        return (
          <button key={s.key} onClick={() => onAdvance(it, s.key, "completed")}
            data-testid={`my-action-${it.id}-${s.key}`}
            className={`inline-flex items-center justify-center px-2 h-7 font-mono text-[10px] uppercase tracking-wider text-white transition-colors ${s.cls}`}>
            {s.btn}
          </button>
        );
      })}
    </div>
  );
}

// ───────── EPIFI-style BOQ — image, wall, dims, stage pipeline ─────────
const STAGE_COLS = [
  { k: "design_approval", label: "Design Approval" },
  { k: "site_audit", label: "Site Audit" },
  { k: "pre_press", label: "Pre-Press" },
  { k: "sample_mockup", label: "Sample/Mockup" },
  { k: "files_received", label: "Files" },
  { k: "production_progress", label: "Production" },
  { k: "delivery_progress", label: "Delivery" },
  { k: "installation_progress", label: "Installation" },
];

const STAGE_VALUES = [
  { v: "pending", label: "Pending", cls: "bg-zinc-100 text-zinc-600 border-zinc-300" },
  { v: "in_progress", label: "In Progress", cls: "bg-amber-50 text-amber-700 border-amber-300" },
  { v: "done", label: "Done", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
  { v: "na", label: "N/A", cls: "bg-zinc-50 text-zinc-400 border-zinc-200" },
];

function StagePill({ value, onChange, locked }) {
  const conf = STAGE_VALUES.find((s) => s.v === value) || STAGE_VALUES[0];
  if (locked) {
    return <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider border ${conf.cls}`}>{conf.label}</span>;
  }
  return (
    <select value={value || "pending"} onChange={(e) => onChange(e.target.value)}
            className={`h-7 px-1.5 text-[10px] font-mono uppercase tracking-wider border outline-none ${conf.cls}`}>
      {STAGE_VALUES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
    </select>
  );
}

function emptyBOQ() {
  return {
    wall_no: "", material_name: "", description: "", artwork_image_url: "",
    height: 0, width: 0, size: "", dimension: "", location: "",
    quantity_required: 1, total_quantity: "", total_quantity_override: false,
    rate: 0, unit: "Nos",
    production_type: "in_house",
    accounts_only: false,
    category: "", sku: "", design_ref: "", spec: "", unit_cost: 0,
    hsn_code: "", hsn_description: "",
    status: "not_started",
    design_approval: "pending", site_audit: "pending", pre_press: "pending",
    sample_mockup: "pending", files_received: "pending",
    production_progress: "pending", delivery_progress: "pending", installation_progress: "pending",
    installation_timeline: "", remarks: "",
  };
}

function DesignerCell({ it, childDesigners = [] }) {
  // Msg 900 — designers are assigned PER WALL. The line (collapsed) shows the
  // compiled set of distinct designers across its walls; no line-level assign.
  const names = [...new Set(childDesigners.filter(Boolean))];
  if (it.is_scope_parent) {
    return names.length ? (
      <div className="flex flex-wrap gap-1" data-testid={`boq-designer-compiled-${it.id}`}>
        {names.map((n, i) => (
          <span key={i} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm bg-stone-100 text-[11px] text-stone-700">
            <User className="w-3 h-3 text-stone-400" strokeWidth={1.5} /> {n}
          </span>
        ))}
      </div>
    ) : (
      <span className="inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-amber-700"
            data-testid={`boq-designer-pending-${it.id}`}>
        <User className="w-3.5 h-3.5" strokeWidth={1.5} /> Assign in walls
      </span>
    );
  }
  return (
    <span className="text-[11px] text-stone-400" data-testid={`boq-designer-none-${it.id}`}>
      Sub-classify to assign
    </span>
  );
}

function scopeBadgeCls(status) {
  if (status === "red") return "bg-rose-100 text-rose-700 border-rose-300";
  if (status === "amber") return "bg-amber-100 text-amber-700 border-amber-300";
  if (status === "green") return "bg-emerald-100 text-emerald-700 border-emerald-300";
  return "bg-zinc-100 text-zinc-600 border-zinc-300";
}

// UPR (project reference) number — set once by the Scope user, shown read-only to everyone.
function UtrBar({ code, utr, me, onSaved }) {
  const canEdit = ["scope_user", "admin", "management"].includes(me?.role);
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(utr || "");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setVal(utr || ""); }, [utr]);
  async function save() {
    if (!val.trim()) { toast.error("Enter the UPR number"); return; }
    if (!reason.trim()) { toast.error("A reason is required to set or change the UPR."); return; }
    setSaving(true);
    try {
      await api.patch(`/projects/${code}/utr`, { utr_no: val.trim(), reason: reason.trim() });
      toast.success("UPR number saved");
      refreshUprMap();
      setEditing(false); setReason("");
      onSaved();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed to save UPR"); }
    finally { setSaving(false); }
  }
  return (
    <div className="card-flat p-3.5 mb-6 flex flex-wrap items-center gap-3" data-testid="project-utr-bar">
      <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">UPR No.</span>
      {editing ? (
        <>
          <Input value={val} onChange={(e) => setVal(e.target.value)} autoFocus
                 placeholder="Enter UPR (project reference)"
                 className="h-9 max-w-xs font-mono" data-testid="project-utr-input" />
          <Input value={reason} onChange={(e) => setReason(e.target.value)}
                 placeholder="Reason for setting / changing (required)"
                 className="h-9 max-w-sm" data-testid="project-utr-reason" />
          <Btn onClick={save} disabled={saving} data-testid="project-utr-save">{saving ? "Saving…" : "Save"}</Btn>
          <Btn variant="secondary" onClick={() => { setVal(utr || ""); setReason(""); setEditing(false); }}>Cancel</Btn>
        </>
      ) : (
        <>
          {utr ? (
            <span className="font-mono text-sm font-semibold text-zinc-900 px-2.5 py-1 rounded-md bg-emerald-50 border border-emerald-200" data-testid="project-utr-value">{utr}</span>
          ) : (
            <span className="text-sm text-zinc-400 italic" data-testid="project-utr-empty">Not assigned yet</span>
          )}
          {canEdit ? (
            <button onClick={() => setEditing(true)} data-testid="project-utr-edit"
                    className="text-xs font-medium text-blue-600 hover:text-blue-800">
              {utr ? "Change" : "Assign UPR"}
            </button>
          ) : (
            <span className="text-[11px] text-zinc-400">Only the Scope user, Admin or Management can change this</span>
          )}
        </>
      )}
    </div>
  );
}



function VariationModal({ code, onClose, onSaved }) {
  const [f, setF] = useState({ material_name: "", quantity_required: "", unit: "sqft", description: "", variation_reason: "" });
  const [req, setReq] = useState({ site: true, designer: true, prepress: true, production: true });
  const [emailUrl, setEmailUrl] = useState("");
  const [emailName, setEmailName] = useState("");
  const [saving, setSaving] = useState(false);
  const tReq = (k) => setReq((r) => ({ ...r, [k]: !r[k] }));
  async function save(e) {
    e.preventDefault();
    if (!f.material_name.trim()) { toast.error("Enter the variation item name"); return; }
    const qty = parseFloat(f.quantity_required || "0");
    if (!qty || qty <= 0) { toast.error("Enter the scope quantity (e.g. 5000 sqft or 50 sqm)"); return; }
    if (!f.unit.trim()) { toast.error("Select the scope unit"); return; }
    setSaving(true);
    try {
      await api.post(`/projects/${code}/boq/variation`, {
        material_name: f.material_name.trim(),
        quantity_required: qty,
        unit: f.unit,
        description: f.description.trim(),
        variation_reason: f.variation_reason.trim(),
        site_measure_required: req.site,
        designer_required: req.designer,
        prepress_required: req.prepress,
        production_required: req.production,
        email_url: emailUrl,
        email_name: emailName,
      });
      toast.success(emailUrl
        ? "Variation added with email confirmation. Management & Scope notified to chase the PO."
        : "Variation added. Management & Scope users notified to raise the PO for this scope.");
      onSaved();
    } catch (err) { toast.error(fmtErr(err.response?.data?.detail)); }
    finally { setSaving(false); }
  }
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
      <form onClick={(e) => e.stopPropagation()} onSubmit={save}
            className="bg-white rounded-2xl w-full max-w-lg p-6 space-y-4" data-testid="variation-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-xl flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-violet-600" /> Add Variation / Additional Scope
          </h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm text-zinc-500">
          New scope discovered during execution. <b>Quantity is mandatory</b> — Management & Scope users will be notified to raise the PO for exactly this much scope.
        </p>
        <Field label="Variation item" required>
          <Input value={f.material_name} onChange={(e) => setF({ ...f, material_name: e.target.value })}
                 placeholder="e.g. New Signage Requirement" data-testid="variation-name" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Scope quantity" required>
            <Input type="number" min="0" step="0.01" value={f.quantity_required} required
                   placeholder="e.g. 5000"
                   onChange={(e) => setF({ ...f, quantity_required: e.target.value })} data-testid="variation-qty" />
          </Field>
          <Field label="Unit" required>
            <select value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })}
                    data-testid="variation-unit"
                    className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-violet-300">
              <option value="sqft">Square Feet (sqft)</option>
              <option value="sqm">Square Meters (sqm)</option>
              <option value="Rft">Running Feet (Rft)</option>
              <option value="Rmt">Running Meters (Rmt)</option>
              <option value="Nos">Numbers (Nos)</option>
            </select>
          </Field>
        </div>
        <p className="text-[11px] text-zinc-400 italic -mt-1">
          The commercial value (rate / amount) is set by the Scope User when they upload the New/Amended PO.
        </p>
        <Field label="Scope description">
          <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
                    rows={2} placeholder="Describe this scope — what is to be done, specs, finish, etc."
                    className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    data-testid="variation-description" />
        </Field>
        <Field label="Reason / where discovered">
          <textarea value={f.variation_reason} onChange={(e) => setF({ ...f, variation_reason: e.target.value })}
                    rows={2} className="w-full text-sm rounded-lg border border-zinc-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-300"
                    data-testid="variation-reason" />
        </Field>
        <div className="rounded-xl border border-zinc-200 bg-zinc-50 p-3" data-testid="variation-stages">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Stages required for this scope</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              ["site", "Site measurement"],
              ["designer", "Designer"],
              ["prepress", "Pre-press"],
              ["production", "Production file"],
            ].map(([k, label]) => (
              <label key={k} className="inline-flex items-center gap-2 text-sm text-zinc-700 cursor-pointer"
                     data-testid={`variation-req-${k}`}>
                <input type="checkbox" checked={req[k]} onChange={() => tReq(k)}
                       data-testid={`variation-req-${k}-checkbox`} />
                {label}
              </label>
            ))}
          </div>
          <p className="text-[11px] text-zinc-400 mt-2">
            A variation's journey ends once the <b>production file reaches the production user</b> — no delivery challan or installation. Uncheck any stage that isn't needed.
          </p>
        </div>
        <Field label="Email confirmation (optional)">
          <FileUpload value={emailUrl}
                      onChange={(url) => { setEmailUrl(url || ""); setEmailName(url ? (url.split("/").pop() || "Email confirmation") : ""); }}
                      testid="variation-email-upload" />
          <p className="text-[11px] text-zinc-400 mt-1">
            Attach the client's email confirming this extra scope. Scope &amp; Management will see it while they chase the actual PO.
          </p>
        </Field>
        <div className="flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={saving} data-testid="variation-save">{saving ? "Adding…" : "Add variation"}</Btn>
        </div>
      </form>
    </div>
  );
}


// Msg 931 — clean, collapsible active-mode BOQ. Each line item is a card with
// its own progress bar + "My Action"; expanding reveals the line pipeline and
// its per-wall scope panel (measurements, designers, per-wall progress).
const _ACTIVE_STAGE_KEYS = ["design_approval", "pre_press", "sample_mockup", "files_received",
                            "production_progress", "delivery_progress", "installation_progress"];
function lineProgressPct(it) {
  const keys = it.production_type === "outsourced"
    ? ["design_approval", "pre_press", "sample_mockup", "files_received"]
    : _ACTIVE_STAGE_KEYS;
  const done = keys.filter((k) => isCompleted(it[k])).length;
  return { done, total: keys.length, pct: keys.length ? Math.round((done / keys.length) * 100) : 0 };
}

// A main scope (scope_parent) carries NO stage data of its own — the walls
// (sub-scopes) under it do. So its progress must be averaged from its children,
// otherwise a fully-completed sub-scope still shows the parent at 0%.
function scopeParentPct(children) {
  if (!children || !children.length) return 0;
  const sum = children.reduce((a, c) => a + lineProgressPct(c).pct, 0);
  return Math.round(sum / children.length);
}

// Aggregate the per-wall pipeline stages up to the parent: a stage is Complete
// only when every child is done/na, In-Progress when any child has started, else
// Pending. Built over the union of child stages (in-house + outsourced mix).
function aggregateChildStages(children) {
  const byKey = {}; const order = [];
  (children || []).forEach((c) => {
    derivePipelineStages(c).forEach((s) => {
      if (!byKey[s.key]) { byKey[s.key] = { label: s.label, vals: [] }; order.push(s.key); }
      byKey[s.key].vals.push(s.v);
    });
  });
  return order.map((k) => {
    const { label, vals } = byKey[k];
    const allNa = vals.every(PIPELINE_NA);
    const allDone = vals.every(PIPELINE_DONE);
    const anyTouched = vals.some((v) => PIPELINE_INPROG(v) || PIPELINE_DONE(v));
    const v = allNa ? "na" : allDone ? "completed" : anyTouched ? "in_progress" : "pending";
    return { key: k, label, v };
  });
}

function BoqActiveCard({ it, idx, code, me, project, canSeeMoney, reload, patchStage,
                         designerOptions, canAssignDesigner, assignDesigner, onEdit,
                         prepressOptions, canAssignPrepress, assignWallPrepress, childLines = [] }) {
  const [open, setOpen] = useState(false);
  const isParent = it.is_scope_parent && childLines.length > 0;
  const pct = isParent ? scopeParentPct(childLines) : lineProgressPct(it).pct;
  const barClr = pct === 100 ? "bg-emerald-500" : pct > 0 ? "bg-blue-500" : "bg-zinc-300";
  const thumb = it.approved_design_file?.file_url || it.artwork_image_url;
  const breach = it.scope_status === "red" && !it.breach_resolved;
  const isVar = it.is_variation && it.variation_status !== "approved";
  return (
    <div className={`card-flat overflow-hidden transition-shadow hover:shadow-sm
      ${breach || (it.is_variation && it.variation_status === "pending_po") ? "border-l-4 border-l-rose-500" : isVar ? "border-l-4 border-l-violet-500" : ""}`}
      data-testid={`boq-card-${it.id}`}>
      <div className="flex items-center gap-3 p-3 sm:p-4">
        <button onClick={() => setOpen((o) => !o)} data-testid={`boq-card-toggle-${it.id}`}
                className="flex items-center gap-3 min-w-0 flex-1 text-left">
          <ChevronDown className={`w-4 h-4 text-zinc-400 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
          {thumb ? (
            <img src={thumb} alt="" className="w-11 h-11 rounded-lg object-cover border border-zinc-200 shrink-0" />
          ) : (
            <div className="w-11 h-11 rounded-lg bg-zinc-50 border border-zinc-200 flex items-center justify-center shrink-0">
              <ImageIcon className="w-4 h-4 text-zinc-300" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-xs font-semibold text-blue-700">{it.wall_no || `#${idx + 1}`}</span>
              <span className="font-medium text-sm text-zinc-900 truncate">{it.material_name}</span>
              {it.production_type === "outsourced" && (
                <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-violet-50 text-violet-700 border border-violet-200">
                  <ExternalLink className="w-2.5 h-2.5" /> Outsourced
                </span>
              )}
              {it.accounts_only && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid={`boq-accounts-only-${it.id}`}>
                  <IndianRupee className="w-2.5 h-2.5" /> Accounts only
                </span>
              )}
              {breach && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200">
                  <AlertTriangle className="w-2.5 h-2.5" /> Scope exceeded
                </span>
              )}
              {it.is_variation && it.variation_status === "pending_po" && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200"
                      data-testid={`boq-variation-perm-${it.id}`}>
                  <Lock className="w-2.5 h-2.5" /> Scope user permission required
                </span>
              )}
              {it.is_variation && it.variation_status === "po_uploaded" && (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-amber-50 text-amber-700 border border-amber-200"
                      data-testid={`boq-variation-po-${it.id}`}>
                  <Clock className="w-2.5 h-2.5" /> PO uploaded · awaiting approval
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <div className="h-1.5 flex-1 max-w-[240px] rounded-full bg-zinc-100 overflow-hidden">
                <div className={`h-full transition-all ${barClr}`} style={{ width: `${pct}%` }} data-testid={`boq-card-progress-${it.id}`} />
              </div>
              <span className="text-[11px] font-mono text-zinc-500 tabular-nums">{pct}%</span>
              <span className="text-[11px] text-zinc-400 inline-flex items-center gap-1">
                <Layers className="w-3 h-3" /> {it.wall_count || 0}
              </span>
            </div>
          </div>
        </button>
        <div className="shrink-0 flex items-center gap-1.5">
          {onEdit && (
            <button onClick={onEdit} title="Edit line (Scope User)"
                    className="h-8 w-8 flex items-center justify-center rounded-lg border border-zinc-200 hover:bg-zinc-100 text-zinc-500"
                    data-testid={`boq-edit-${it.id}`}>
              <Edit3 className="w-3.5 h-3.5" />
            </button>
          )}
          <MyActionCell it={it} me={me} project={project} onAdvance={patchStage} />
        </div>
      </div>
      {open && (
        <div className="border-t border-zinc-100" data-testid={`boq-card-body-${it.id}`}>
          {it.accounts_only ? (
            <div className="px-4 py-5 bg-amber-50/40 flex items-start gap-3" data-testid={`boq-accounts-only-body-${it.id}`}>
              <IndianRupee className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-sm text-zinc-600">
                <div className="font-medium text-zinc-800">Accounts-only line — no operational scope.</div>
                This is a purely financial line. It skips Design, Pre-Press &amp; Production and isn't sub-divided into walls. It only needs a PO (raised in the Purchase Orders portal) and appears in the Accounts commercials.
              </div>
            </div>
          ) : (
            <>
              <div className="px-3 sm:px-4 py-3 overflow-x-auto bg-zinc-50/40">
                <PipelineStrip it={it} stages={isParent ? aggregateChildStages(childLines) : undefined} />
              </div>
              <BoqScopePanel line={it} code={code} me={me} canSeeMoney={canSeeMoney} onChange={reload}
                             designerOptions={designerOptions} canAssign={canAssignDesigner}
                             onAssignDesigner={(wid, did, reason, instruction) => assignDesigner(wid, did, reason || "", instruction || "")}
                             prepressOptions={prepressOptions} canAssignPrepress={canAssignPrepress}
                             onAssignPrepress={assignWallPrepress} />
            </>
          )}
        </div>
      )}
    </div>
  );
}

function BOQ({ code, items, reload, project, me, users = [] }) {
  const [versions, setVersions] = useState([]);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [vNotes, setVNotes] = useState("");
  const [editing, setEditing] = useState(null);  // null = closed; {} = new; {id, ...} = edit
  const [showAIImport, setShowAIImport] = useState(false);
  const [zoomImg, setZoomImg] = useState(null);
  const [expanded, setExpanded] = useState({});   // Msg 893 — per-line scope panel
  const [showVariation, setShowVariation] = useState(false);
  const locked = !!project?.boq_locked;
  const isDraft = (project?.lifecycle_state || "active") === "draft";
  const canVariation = !isDraft && ["admin", "management", "csm"].includes(me?.role);
  // Msg 894 — only the Scope User (+ admin/management) own the master BOQ scope &
  // its commercial value. CSM never sees money — operational scope only.
  const canEditMaster = ["admin", "management", "scope_user"].includes(me?.role);
  const canSeeMoney = ["admin", "management", "scope_user", "accounts"].includes(me?.role);
  // The Scope User (or Admin) may correct master-line mistakes even after the
  // project goes active — spelling, qty, unit, in-house/outsourced routing.
  const canLineEdit = isDraft ? canEditMaster : ["admin", "scope_user"].includes(me?.role);

  useEffect(() => { api.get(`/projects/${code}/boq-versions`).then((r) => setVersions(r.data)); }, [code, items]);

  function save(form) {
    const tqRaw = form.total_quantity;
    const body = {
      ...form,
      quantity_required: Number(form.quantity_required) || 0,
      height: Number(form.height) || 0,
      width: Number(form.width) || 0,
      rate: Number(form.rate) || 0,
      unit_cost: Number(form.rate) || Number(form.unit_cost) || 0,
      total_quantity: (tqRaw === "" || tqRaw === null || tqRaw === undefined) ? null : Number(tqRaw),
      total_quantity_override: !!form.total_quantity_override,
    };
    const promise = form.id
      ? api.patch(`/projects/${code}/boq/${form.id}`, body)
      : api.post(`/projects/${code}/boq`, body);
    promise
      .then(() => { reload(); setEditing(null); toast.success(form.id ? "Updated" : "Added"); })
      .catch((e) => toast.error(fmtErr(e.response?.data?.detail)));
  }

  async function patchStage(it, key, value) {
    try {
      await api.patch(`/projects/${code}/boq/${it.id}`, { ...it, [key]: value });
      reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  async function del(id) {
    if (!window.confirm("Delete this BOQ line?")) return;
    try { await api.delete(`/projects/${code}/boq/${id}`); reload(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  // Msg 885 — per-line designer ownership. Dropdown lists ONLY designers already
  // on the project's team; assignable by admin/management/assigned-CSM.
  const teamDesignerIds = (() => {
    const d = project?.assignments?.designer;
    return Array.isArray(d) ? d : (d ? [d] : []);
  })();
  const designerOptions = teamDesignerIds
    .map((id) => users.find((u) => u.id === id))
    .filter(Boolean);
  const isAssignedCsm = (() => {
    const c = project?.assignments?.csm;
    const arr = Array.isArray(c) ? c : (c ? [c] : []);
    return arr.includes(me?.id);
  })();
  const canAssignDesigner = me?.role === "admin" || me?.role === "management"
    || (me?.role === "csm" && isAssignedCsm);

  // Msg 899 — per-wall pre-press ownership. Assignable by admin/management/assigned-PSM.
  const teamPrepressIds = (() => {
    const d = project?.assignments?.prepress;
    return Array.isArray(d) ? d : (d ? [d] : []);
  })();
  const prepressOptions = teamPrepressIds
    .map((id) => users.find((u) => u.id === id))
    .filter(Boolean);
  const isAssignedPsm = (() => {
    const c = project?.assignments?.psm;
    const arr = Array.isArray(c) ? c : (c ? [c] : []);
    return arr.includes(me?.id);
  })();
  const canAssignPrepress = me?.role === "admin" || me?.role === "management"
    || (me?.role === "psm" && isAssignedPsm);

  async function assignWallPrepress(wid, prepressId) {
    try {
      await api.patch(`/projects/${code}/boq/${wid}/prepress`, { prepress_id: prepressId });
      reload();
      toast.success(prepressId ? "Pre-press assigned" : "Pre-press cleared");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  async function assignDesigner(bid, designerId, reason = "", instruction = "") {
    try {
      await api.patch(`/projects/${code}/boq/${bid}/designer`,
                      { designer_id: designerId, reason, design_instruction: instruction });
      reload();
      toast.success(designerId ? "Designer assigned" : "Designer cleared");
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  async function saveVersion(finalize) {
    try {
      const { data } = await api.post(`/projects/${code}/boq-versions`, { notes: vNotes, finalize });
      toast.success(`Saved v${data.version_no}${finalize ? " (locked)" : ""}`);
      setShowVersionModal(false); setVNotes(""); reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  async function unlock() {
    try { await api.post(`/projects/${code}/boq-unlock`); toast.success("BOQ unlocked — CSM can now add lines"); reload(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function relock() {
    try { await api.post(`/projects/${code}/boq-lock`); toast.success("BOQ locked"); reload(); }
    catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  // Main spine (7 stages) — aligned with backend _compute_project_progress; site_audit is parallel.
  const MAIN_STAGE_KEYS = ["design_approval", "pre_press", "sample_mockup", "files_received",
                           "production_progress", "delivery_progress", "installation_progress"];
  const workItems = items.filter((it) => !it.is_scope_parent);
  const stagesDone = workItems.reduce((acc, it) => {
    MAIN_STAGE_KEYS.forEach((k) => { if (isCompleted(it[k])) acc++; });
    return acc;
  }, 0);
  const stagesTotal = workItems.length * MAIN_STAGE_KEYS.length;
  const pct = stagesTotal ? Math.round((stagesDone / stagesTotal) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="card-flat p-5 flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="font-display font-bold tracking-tight text-lg">Bill of Quantities</div>
          <div className="text-xs text-zinc-500 mt-1 flex flex-wrap items-center gap-2">
            <span className="font-mono">{items.length} LINES</span>
            <span className="text-zinc-300">·</span>
            <span className="font-mono">{stagesDone}/{stagesTotal} MAIN STAGES DONE ({pct}%)</span>
            {versions.length > 0 && <><span className="text-zinc-300">·</span><span className="font-mono">v{versions[0].version_no} latest</span></>}
            {locked && <span className="ml-1 px-2 py-0.5 bg-rose-50 text-rose-700 border border-rose-200 font-mono uppercase text-[10px] tracking-widest" data-testid="boq-locked-badge">🔒 Locked</span>}
          </div>
          <div className="h-1 bg-zinc-200 mt-3 w-64 max-w-full">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <div className="flex gap-2">
          {/* Msg 893/894 — the master BOQ scope is built by the Scope User in the
              Draft stage (or Admin anytime). CSM/PSM/etc. NEVER see this. */}
          {canEditMaster && (isDraft || me?.role === "admin") && (
            <>
              {(me?.role === "admin" || me?.role === "scope_user") && (
                <Btn variant="secondary" onClick={() => setShowAIImport(true)} data-testid="ai-import-boq-btn"
                     className="border-violet-300 text-violet-700 hover:bg-violet-50">
                  <Sparkles className="w-4 h-4" /> Import with AI
                </Btn>
              )}
              <Btn onClick={() => setEditing(emptyBOQ())} data-testid="add-boq-btn"><Plus className="w-4 h-4" /> Add Line</Btn>
            </>
          )}
          {!isDraft && me?.role === "admin" && locked && (
            <Btn variant="secondary" onClick={unlock} data-testid="boq-unlock-btn">
              <Unlock className="w-4 h-4" /> Unlock BOQ
            </Btn>
          )}
          {!isDraft && me?.role === "admin" && !locked && (
            <Btn variant="secondary" onClick={relock} data-testid="boq-lock-btn">
              <Lock className="w-4 h-4" /> Re-lock BOQ
            </Btn>
          )}
          {isDraft && <Btn variant="secondary" onClick={() => setShowVersionModal(true)} data-testid="boq-version-btn">Save Version</Btn>}
          {canVariation && (
            <Btn variant="secondary" onClick={() => setShowVariation(true)} data-testid="add-variation-btn"
                 className="border-violet-300 text-violet-700 hover:bg-violet-50">
              <Plus className="w-4 h-4" /> Add Variation
            </Btn>
          )}
        </div>
      </div>

      {!isDraft && <BoqStageFunnel items={items.filter((it) => !it.accounts_only)} />}

      {/* Active mode — clean collapsible accordion (Msg 931). Draft keeps the
          full editable table below. Accounts-only lines (design fee etc.) are
          purely financial — excluded from the operational BOQ + progress. */}
      {!isDraft && (
        items.filter((it) => !it.parent_id && !it.accounts_only).length === 0 ? (
          <div className="card-flat p-12 text-center text-zinc-400">
            <div className="font-mono text-xs uppercase tracking-widest">No BOQ lines yet</div>
            <div className="mt-1">BOQ has not been finalized yet.</div>
          </div>
        ) : (
          <div className="space-y-2" data-testid="boq-active-list">
            {items.filter((it) => !it.parent_id && !it.accounts_only).map((it, idx) => (
              <BoqActiveCard key={it.id} it={it} idx={idx} code={code} me={me} project={project}
                             childLines={items.filter((c) => c.parent_id === it.id)}
                             onEdit={canLineEdit ? () => setEditing(it) : undefined}
                             canSeeMoney={canSeeMoney} reload={reload} patchStage={patchStage}
                             designerOptions={designerOptions} canAssignDesigner={canAssignDesigner} assignDesigner={assignDesigner}
                             prepressOptions={prepressOptions} canAssignPrepress={canAssignPrepress} assignWallPrepress={assignWallPrepress} />
            ))}
          </div>
        )
      )}

      {/* Table (draft build mode only) */}
      {isDraft && (
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm border-separate" style={{ borderSpacing: 0, minWidth: isDraft ? "1400px" : "880px" }}>
          <thead className="bg-zinc-900 text-white text-[10px] font-mono uppercase tracking-widest sticky top-0">
            <tr>
              <th className="text-left p-2 sticky left-0 bg-zinc-900 z-10 border-r border-zinc-700">Sr</th>
              {!isDraft && <th className="text-center p-2 bg-emerald-700 min-w-[140px]">My Action</th>}
              <th className="text-left p-2">Wall #</th>
              <th className="text-left p-2">Description</th>
              {!isDraft && <th className="text-left p-2 min-w-[150px]">Designer</th>}
              <th className="text-center p-2">Artwork</th>
              <th className="text-right p-2">H × W</th>
              <th className="text-right p-2">Qty / Unit</th>
              {isDraft ? (
                STAGE_COLS.map((c) => (
                  <th key={c.k} className="text-center p-2 whitespace-nowrap">{c.label}</th>
                ))
              ) : (
                <th className="text-center p-2 whitespace-nowrap min-w-[280px]">Pipeline</th>
              )}
              {isDraft && <th className="text-left p-2">Timeline</th>}
              {isDraft && <th className="text-left p-2">Remarks</th>}
              {isDraft && <th className="text-right p-2 sticky right-0 bg-zinc-900 z-10 border-l border-zinc-700"></th>}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={isDraft ? (6 + STAGE_COLS.length + 3) : 8} className="p-12 text-center text-zinc-400">
                <div className="font-mono text-xs uppercase tracking-widest">No BOQ lines yet</div>
                <div className="mt-1">{isDraft ? "Click \"Add Line\" to start building the BOQ." : "BOQ has not been finalized yet."}</div>
              </td></tr>
            )}
            {items.filter((it) => !it.parent_id).map((it, idx) => {
              const scClr = it.is_variation && it.variation_status !== "approved" ? "border-l-4 border-l-violet-500"
                : it.scope_status === "red" ? "border-l-4 border-l-rose-500"
                : it.scope_status === "amber" ? "border-l-4 border-l-amber-400"
                : it.scope_status === "green" ? "border-l-4 border-l-emerald-400" : "";
              return (
              <Fragment key={it.id}>
              <tr className={`border-t border-zinc-200 hover:bg-blue-50/30 ${scClr}`} data-testid={`boq-row-${it.id}`}>
                <td className="p-2 font-mono text-xs sticky left-0 bg-white border-r border-zinc-200 z-[1]">{idx + 1}</td>
                {!isDraft && (
                  <td className="p-2 text-center bg-emerald-50/40 border-x border-emerald-200">
                    <MyActionCell it={it} me={me} project={project} onAdvance={patchStage} />
                  </td>
                )}
                <td className="p-2 font-mono font-semibold text-blue-700">{it.wall_no || "—"}</td>
                <td className="p-2 min-w-[220px] max-w-sm align-top">
                  <div className="font-medium leading-tight break-words">{it.material_name}</div>
                  {it.description && <div className="text-xs text-zinc-500 mt-0.5 leading-snug whitespace-pre-wrap break-words">{it.description}</div>}
                  {it.location && <div className="text-[10px] text-zinc-500 mt-0.5">📍 {it.location}</div>}
                  {it.sku && <div className="text-[10px] font-mono text-zinc-400 mt-0.5">{it.sku}</div>}
                  {it.production_type === "outsourced" && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider mt-1 bg-violet-50 text-violet-700 border border-violet-200"
                          data-testid={`boq-outsourced-${it.id}`}
                          title="Outsourced — workflow ends at the Pre-Press production file">
                      <ExternalLink className="w-2.5 h-2.5" /> Outsourced
                    </span>
                  )}
                  {/* Msg 1064/1065 — PO status badge only for admin/management/accounts.
                      Msg 894 — CSM removed (operational-scope only). The backend
                      already returns linked_po=null to other roles. */}
                  {!isDraft && ["admin", "management", "accounts"].includes(me?.role) && (
                    it.linked_po ? (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider mt-1 bg-emerald-50 text-emerald-700 border border-emerald-200"
                            data-testid={`boq-po-ok-${it.id}`}
                            title={`Tagged to ${it.linked_po.po_number || it.linked_po.doc_type}`}>
                        <CheckCircle2 className="w-2.5 h-2.5" />
                        {it.linked_po.po_number || (it.linked_po.doc_type === "email" ? "EMAIL OK" : "PO OK")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider mt-1 bg-rose-50 text-rose-700 border border-rose-200"
                            data-testid={`boq-po-missing-${it.id}`}
                            title="No PO or email confirmation tagged">
                        ⚠ PO / Email Confirmation Required
                      </span>
                    )
                  )}
                </td>
                {!isDraft && (
                  <td className="p-2 align-top">
                    <DesignerCell it={it}
                                  childDesigners={items.filter((c) => c.parent_id === it.id).map((c) => c.designer_name)} />
                  </td>
                )}
                <td className="p-2 text-center">
                  {/* Msg 1060 — once client approves a design, the artwork cell
                      switches from the tentative sketch to the actual approved
                      design thumbnail (with a small green "approved" pill). */}
                  {(() => {
                    const approved = it.approved_design_file;
                    const previewUrl = approved?.file_url || it.artwork_image_url;
                    const isApproved = !!approved?.file_url;
                    if (!previewUrl) {
                      return (
                        <div className="w-16 h-16 mx-auto bg-zinc-50 border border-dashed border-zinc-300 flex items-center justify-center">
                          <ImageIcon className="w-5 h-5 text-zinc-300" />
                        </div>
                      );
                    }
                    return (
                      <div className="relative inline-block">
                        <button onClick={() => setZoomImg(previewUrl)} className="block mx-auto"
                                data-testid={`boq-img-${it.id}`}
                                title={isApproved ? `Approved · V${approved?.version_number || "?"}` : "Tentative draft"}>
                          <img src={previewUrl} alt={isApproved ? "approved design" : "tentative artwork"}
                               className={`w-16 h-16 object-cover border transition-colors ${isApproved
                                 ? "border-emerald-400 ring-2 ring-emerald-100 hover:border-emerald-600"
                                 : "border-zinc-300 hover:border-blue-500"}`} />
                        </button>
                        {isApproved ? (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-sm text-[8px] font-mono uppercase tracking-wider bg-emerald-600 text-white shadow-sm"
                                data-testid={`boq-img-approved-${it.id}`}>
                            <CheckCircle2 className="w-2.5 h-2.5" /> approved
                          </span>
                        ) : (
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 inline-block px-1.5 py-0.5 rounded-sm text-[8px] font-mono uppercase tracking-wider bg-amber-100 text-amber-800 border border-amber-200"
                                data-testid={`boq-img-tentative-${it.id}`}>
                            draft
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td className="p-2 text-right font-mono text-xs whitespace-nowrap">
                  {/* Phase 34 r5 (Msg 760): tentative H×W until PSM enters
                      site measurements. Once they do, the actual numbers
                      take over and the 'tentative' badge disappears. */}
                  {it.site_actual_height && it.site_actual_width ? (
                    <span className="inline-flex items-center gap-1">
                      <span className="text-emerald-700 font-semibold">
                        {it.site_actual_height} × {it.site_actual_width}
                      </span>
                      <span className="text-[9px] uppercase text-emerald-600">{it.site_actual_unit || ""}</span>
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-zinc-500">
                      {(it.height || 0)}″ × {(it.width || 0)}″
                      <span className="text-[8px] uppercase tracking-wider px-1 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">
                        tentative
                      </span>
                    </span>
                  )}
                </td>
                <td className="p-2 text-right font-mono whitespace-nowrap align-top">
                  <div className="font-bold">{it.quantity_required}<span className="text-xs text-zinc-500 ml-1">{it.unit}</span></div>
                  {it.total_quantity != null && Number(it.total_quantity) !== Number(it.quantity_required) && (
                    <div className="text-[10px] text-zinc-500" data-testid={`boq-total-qty-${it.id}`}>
                      total {it.total_quantity} {it.unit}
                    </div>
                  )}
                  {canSeeMoney && (it.rate || it.amount) ? (
                    <div className="mt-0.5 text-[10px] text-emerald-700" data-testid={`boq-amount-${it.id}`}>
                      ₹{Number(it.rate || 0).toLocaleString("en-IN")}/{it.unit} · <b>₹{Number(it.amount || 0).toLocaleString("en-IN")}</b>
                    </div>
                  ) : null}
                  {!isDraft && (
                    <div className="mt-1 flex flex-col items-end gap-1">
                      {it.is_variation && it.variation_status !== "approved" && (
                        <span data-testid={`variation-badge-${it.id}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider bg-violet-100 text-violet-700 border border-violet-300">
                          Variation · {it.variation_status === "po_uploaded" ? "approve PO" : "needs PO"}
                        </span>
                      )}
                      {!it.is_variation && it.scope_status && it.scope_status !== "na" && (
                        <span data-testid={`scope-badge-${it.id}`}
                              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono uppercase tracking-wider border ${scopeBadgeCls(it.scope_status)}`}>
                          {it.actual_measured_total ?? 0}/{it.quantity_required}{it.scope_status === "red" ? " · exceeded" : it.scope_status === "amber" ? " · 90%+" : ""}
                        </span>
                      )}
                      <button onClick={() => setExpanded((e) => ({ ...e, [it.id]: !e[it.id] }))}
                              data-testid={`scope-toggle-${it.id}`}
                              className="inline-flex items-center gap-1 text-[10px] text-zinc-500 hover:text-blue-600">
                        <Layers className="w-3 h-3" /> {it.wall_count ? `Walls (${it.wall_count})` : "Walls"}
                      </button>
                    </div>
                  )}
                </td>
                {isDraft ? (
                  STAGE_COLS.map((c) => {
                    const byField = ({
                      design_approval: "design_by", pre_press: "pre_press_by",
                      sample_mockup: "sample_by", files_received: "files_by",
                      production_progress: "production_by", delivery_progress: "delivery_by",
                      installation_progress: "install_by",
                    })[c.k];
                    const by = byField ? it[byField] : "";
                    const at = byField ? it[byField.replace("_by", "_at")] : "";
                    // Outsourced lines: the internal Production / Delivery /
                    // Installation stages do not exist — render a muted dash
                    // (never a "N/A" pill) so the grid stays clean.
                    const hiddenForOutsourced = it.production_type === "outsourced"
                      && ["production_progress", "delivery_progress", "installation_progress"].includes(c.k);
                    return (
                      <td key={c.k} className="p-2 text-center" data-testid={`boq-${it.id}-${c.k}`}>
                        {hiddenForOutsourced ? (
                          <span className="text-zinc-300" title="Not part of the outsourced workflow">—</span>
                        ) : (
                          <StagePill value={it[c.k]} locked={locked} onChange={(v) => patchStage(it, c.k, v)} />
                        )}
                        {by && !hiddenForOutsourced && (
                          <div className="text-[9px] font-mono text-zinc-500 mt-1 leading-tight" data-testid={`accountability-${it.id}-${c.k}`}>
                            by {by}
                            {at && <div className="text-zinc-400">{(at || "").slice(5, 10)}</div>}
                          </div>
                        )}
                      </td>
                    );
                  })
                ) : (
                  <td className="p-2 text-center">
                    <PipelineStrip it={it} />
                  </td>
                )}
                {isDraft && <td className="p-2 text-xs whitespace-nowrap">{it.installation_timeline || "—"}</td>}
                {isDraft && <td className="p-2 text-xs max-w-[180px] truncate" title={it.remarks}>{it.remarks || "—"}</td>}
                {(isDraft || canLineEdit) && (
                  <td className="p-2 sticky right-0 bg-white border-l border-zinc-200 z-[1]">
                    <div className="flex gap-1 justify-end">
                      {canLineEdit && (
                        <button onClick={() => setEditing(it)} className="h-7 w-7 flex items-center justify-center hover:bg-zinc-100 text-zinc-600"
                                data-testid={`boq-edit-${it.id}`}>
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!locked && isDraft && (
                        <button onClick={() => del(it.id)} className="h-7 w-7 flex items-center justify-center hover:bg-rose-50 text-rose-600"
                                data-testid={`boq-delete-${it.id}`}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                )}
              </tr>
              {!isDraft && expanded[it.id] && (
                <tr data-testid={`boq-detail-row-${it.id}`}>
                  <td colSpan={99} className="p-0">
                    <BoqScopePanel line={it} code={code} me={me} canSeeMoney={canSeeMoney} onChange={reload}
                                   designerOptions={designerOptions} canAssign={canAssignDesigner}
                                   onAssignDesigner={(wid, did, reason, instruction) => assignDesigner(wid, did, reason || "", instruction || "")}
                                   prepressOptions={prepressOptions} canAssignPrepress={canAssignPrepress}
                                   onAssignPrepress={assignWallPrepress} />
                  </td>
                </tr>
              )}
              </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      )}

      {/* Version history */}
      {versions.length > 0 && (
        <div className="card-flat p-5">
          <div className="font-display font-bold tracking-tight mb-3">Version History</div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <tr><th className="text-left p-2">Version</th><th className="text-left p-2">Items</th><th className="text-left p-2">Notes</th><th className="text-left p-2">By</th><th className="text-left p-2">Status</th></tr>
            </thead>
            <tbody>
              {versions.map((v) => (
                <tr key={v.id} className="border-t border-zinc-100">
                  <td className="p-2 font-mono font-bold">v{v.version_no}</td>
                  <td className="p-2 font-mono">{v.items_count}</td>
                  <td className="p-2 text-xs">{v.notes || "—"}</td>
                  <td className="p-2 text-xs">{v.created_by}</td>
                  <td className="p-2 text-xs font-mono uppercase">{v.locked ? "🔒 Locked" : "Draft"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <BOQEditModal initial={editing} locked={locked && !["admin", "scope_user"].includes(me?.role)} canSeeMoney={canSeeMoney} onClose={() => setEditing(null)} onSave={save} />}

      {showAIImport && (
        <AIImportModal code={code} onClose={() => setShowAIImport(false)}
                       onDone={() => { setShowAIImport(false); reload(); }} />
      )}

      {showVariation && (
        <VariationModal code={code} onClose={() => setShowVariation(false)}
                        onSaved={() => { setShowVariation(false); reload(); }} />
      )}

      {showVersionModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white max-w-md w-full p-6 space-y-3 border border-zinc-200">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">Save BOQ Version</h3>
              <button type="button" onClick={() => setShowVersionModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <Field label="Notes for this version">
              <textarea rows="3" value={vNotes} onChange={(e) => setVNotes(e.target.value)} className="w-full p-3 border border-zinc-300 text-sm" placeholder="Why this version?" />
            </Field>
            <div className="flex gap-2 pt-2 border-t border-zinc-200">
              <Btn onClick={() => saveVersion(false)}>Save Snapshot</Btn>
              <Btn onClick={() => saveVersion(true)} className="bg-emerald-700 hover:bg-emerald-800">Save & Finalize (Lock)</Btn>
              <Btn variant="secondary" onClick={() => setShowVersionModal(false)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}

      {zoomImg && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6 cursor-zoom-out"
             onClick={() => setZoomImg(null)} data-testid="boq-image-zoom">
          <img src={zoomImg} alt="artwork" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}

function AIImportModal({ code, onClose, onDone }) {
  const [step, setStep] = useState("upload");   // upload | parsing | review
  const [fileUrl, setFileUrl] = useState("");
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const cancelledRef = useRef(false);

  useEffect(() => () => { cancelledRef.current = true; }, []);

  async function runParse(url) {
    setStep("parsing");
    cancelledRef.current = false;
    try {
      const { data } = await api.post(`/projects/${code}/boq/ai-import`, { file_url: url });
      const jobId = data.job_id;
      // Poll for the result — extraction runs server-side (decoupled from the
      // ingress proxy timeout). Up to ~3 minutes.
      const started = Date.now();
      while (!cancelledRef.current && Date.now() - started < 180000) {
        await new Promise((r) => setTimeout(r, 2500));
        if (cancelledRef.current) return;
        const { data: job } = await api.get(`/projects/${code}/boq/ai-import/${jobId}`);
        if (job.status === "done") {
          setRows((job.lines || []).map((l, i) => ({ ...l, _key: `${Date.now()}-${i}` })));
          setStep("review");
          toast.success(`AI found ${job.count} line item${job.count === 1 ? "" : "s"}`);
          return;
        }
        if (job.status === "error") {
          toast.error(job.error || "AI import failed");
          setStep("upload"); setFileUrl("");
          return;
        }
      }
      if (!cancelledRef.current) {
        toast.error("AI import is taking too long. Please try again.");
        setStep("upload"); setFileUrl("");
      }
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "AI import failed");
      setStep("upload"); setFileUrl("");
    }
  }

  function setRow(key, patch) {
    setRows((rs) => rs.map((r) => (r._key === key ? { ...r, ...patch } : r)));
  }
  function removeRow(key) {
    setRows((rs) => rs.filter((r) => r._key !== key));
  }

  async function approve() {
    const lines = rows
      .filter((r) => (r.material_name || "").trim())
      .map((r) => ({
        material_name: r.material_name,
        description: r.description || "",
        quantity_required: Number(r.quantity_required) || 0,
        unit: r.unit || "Nos",
        size: r.size || "",
        height: Number(r.height) || 0,
        width: Number(r.width) || 0,
        material_type: r.material_type || "",
        finish: r.finish || "",
        unit_cost: Number(r.unit_cost) || 0,
        production_type: r.production_type === "outsourced" ? "outsourced" : "in_house",
      }));
    if (!lines.length) { toast.error("Add at least one line with an item name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/projects/${code}/boq/bulk`, { lines });
      toast.success(`Added ${data.count} BOQ line${data.count === 1 ? "" : "s"}`);
      onDone();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail) || "Could not add lines");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="ai-import-modal">
      <div className="bg-white w-full max-w-5xl max-h-[92vh] flex flex-col border border-zinc-200">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 flex-shrink-0 bg-gradient-to-r from-violet-50 to-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-600 flex items-center justify-center flex-shrink-0">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-display font-bold text-xl">AI BOQ Import</h3>
              <div className="text-xs text-zinc-500 mt-0.5">Upload your BOQ sheet — AI extracts the line items for your review.</div>
            </div>
          </div>
          <button type="button" onClick={onClose} data-testid="ai-import-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-6 flex-1">
          {step === "upload" && (
            <div className="max-w-lg mx-auto py-6 space-y-5">
              <div className="text-center">
                <div className="font-display font-bold text-lg">Upload your BOQ spreadsheet</div>
                <div className="text-sm text-zinc-500 mt-1">Excel (.xlsx, .xls) or CSV. The AI reads it and pulls out item name, description, qty, size, unit, material, finishing, rate &amp; amount.</div>
              </div>
              <FileUpload value={fileUrl} onChange={(url) => { setFileUrl(url); if (url) runParse(url); }}
                          label="Choose BOQ Excel / CSV file" accept=".xlsx,.xls,.csv"
                          testid="ai-import-file" />
              <div className="flex items-start gap-2 text-[11px] text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-md p-3">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-amber-500" />
                <span>Nothing is added to the project yet — you'll review and edit every line before approving.</span>
              </div>
            </div>
          )}

          {step === "parsing" && (
            <div className="py-20 flex flex-col items-center justify-center gap-4 text-center">
              <Loader2 className="w-10 h-10 text-violet-600 animate-spin" />
              <div className="font-display font-bold text-lg">Reading your BOQ…</div>
              <div className="text-sm text-zinc-500">The AI is understanding the sheet and extracting line items. This usually takes 15–40 seconds — please keep this window open.</div>
            </div>
          )}

          {step === "review" && (
            <div className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">
                  <span className="font-semibold">{rows.length}</span> line item{rows.length === 1 ? "" : "s"} extracted ·
                  <span className="text-zinc-500"> review &amp; edit before adding</span>
                </div>
                <Btn variant="secondary" onClick={() => { setRows([]); setFileUrl(""); setStep("upload"); }} data-testid="ai-import-restart">
                  <Upload className="w-4 h-4" /> Use a different file
                </Btn>
              </div>

              <div className="border border-zinc-200 rounded-md overflow-x-auto">
                <table className="w-full text-xs" style={{ minWidth: "980px" }}>
                  <thead className="bg-zinc-900 text-white font-mono uppercase tracking-wider text-[10px]">
                    <tr>
                      <th className="text-left p-2">Item Name *</th>
                      <th className="text-left p-2">Description</th>
                      <th className="text-right p-2 w-16">Qty</th>
                      <th className="text-left p-2 w-20">Unit</th>
                      <th className="text-left p-2 w-24">Size</th>
                      <th className="text-left p-2 w-24">Material</th>
                      <th className="text-left p-2 w-24">Finishing</th>
                      <th className="text-right p-2 w-20">Rate</th>
                      <th className="text-left p-2 w-28">Production</th>
                      <th className="p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const missing = Array.isArray(r.missing_info) ? r.missing_info : [];
                      return (
                        <tr key={r._key} className="border-t border-zinc-100 align-top" data-testid={`ai-row-${r._key}`}>
                          <td className="p-1.5">
                            <input value={r.material_name || ""} onChange={(e) => setRow(r._key, { material_name: e.target.value })}
                                   className="w-full px-1.5 py-1 border border-zinc-200 rounded text-xs font-medium" />
                            {missing.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {missing.map((m, i) => (
                                  <span key={i} className="inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[9px] font-mono uppercase bg-amber-50 text-amber-700 border border-amber-200">
                                    <AlertTriangle className="w-2.5 h-2.5" />{m}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="p-1.5">
                            <input value={r.description || ""} onChange={(e) => setRow(r._key, { description: e.target.value })}
                                   className="w-full px-1.5 py-1 border border-zinc-200 rounded text-xs" />
                          </td>
                          <td className="p-1.5">
                            <input type="number" value={r.quantity_required ?? ""} onChange={(e) => setRow(r._key, { quantity_required: e.target.value })}
                                   className="w-full px-1.5 py-1 border border-zinc-200 rounded text-xs text-right" />
                          </td>
                          <td className="p-1.5">
                            <input value={r.unit || ""} onChange={(e) => setRow(r._key, { unit: e.target.value })}
                                   className="w-full px-1.5 py-1 border border-zinc-200 rounded text-xs" />
                          </td>
                          <td className="p-1.5">
                            <input value={r.size || ""} onChange={(e) => setRow(r._key, { size: e.target.value })}
                                   className="w-full px-1.5 py-1 border border-zinc-200 rounded text-xs" />
                          </td>
                          <td className="p-1.5">
                            <input value={r.material_type || ""} onChange={(e) => setRow(r._key, { material_type: e.target.value })}
                                   className="w-full px-1.5 py-1 border border-zinc-200 rounded text-xs" />
                          </td>
                          <td className="p-1.5">
                            <input value={r.finish || ""} onChange={(e) => setRow(r._key, { finish: e.target.value })}
                                   className="w-full px-1.5 py-1 border border-zinc-200 rounded text-xs" />
                          </td>
                          <td className="p-1.5">
                            <input type="number" value={r.unit_cost ?? ""} onChange={(e) => setRow(r._key, { unit_cost: e.target.value })}
                                   className="w-full px-1.5 py-1 border border-zinc-200 rounded text-xs text-right" />
                          </td>
                          <td className="p-1.5">
                            <select value={r.production_type || "in_house"} onChange={(e) => setRow(r._key, { production_type: e.target.value })}
                                    className="w-full px-1 py-1 border border-zinc-200 rounded text-xs">
                              <option value="in_house">In-house</option>
                              <option value="outsourced">Outsourced</option>
                            </select>
                          </td>
                          <td className="p-1.5 text-center">
                            <button type="button" onClick={() => removeRow(r._key)} className="text-zinc-400 hover:text-rose-600"
                                    data-testid={`ai-row-remove-${r._key}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {rows.length === 0 && (
                      <tr><td colSpan={10} className="p-8 text-center text-zinc-400 text-sm">All rows removed. Upload a different file to start over.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {step === "review" && (
          <div className="flex gap-2 p-5 border-t border-zinc-200 bg-zinc-50 flex-shrink-0">
            <Btn onClick={approve} disabled={busy || rows.length === 0} data-testid="ai-import-approve"
                 className="bg-violet-600 hover:bg-violet-700">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {busy ? "Adding…" : `Approve & Add ${rows.filter((r) => (r.material_name || "").trim()).length} Lines`}
            </Btn>
            <Btn variant="secondary" onClick={onClose} data-testid="ai-import-reject">Reject &amp; Close</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

function BOQEditModal({ initial, locked, canSeeMoney = false, onClose, onSave }) {
  const [f, setF] = useState(initial);
  const isNew = !f.id;
  function set(k, v) { setF((s) => ({ ...s, [k]: v })); }
  // Msg 894 — AMOUNT (₹) auto-computes from RATE × TOTAL QUANTITY (read-only).
  // TOTAL QUANTITY defaults to QUANTITY until the PSM's measured total syncs in.
  const effTotalQty = (f.total_quantity === "" || f.total_quantity === null || f.total_quantity === undefined)
    ? (Number(f.quantity_required) || 0)
    : (Number(f.total_quantity) || 0);
  const computedAmount = (Number(f.rate) || 0) * effTotalQty;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white max-w-3xl w-full max-h-[92vh] flex flex-col border border-zinc-200">
        <div className="flex items-center justify-between p-6 border-b border-zinc-200 flex-shrink-0">
          <div>
            <h3 className="font-display font-bold text-xl">{isNew ? "Add BOQ Line" : "Edit BOQ Line"}</h3>
            <div className="text-xs text-zinc-500 font-mono uppercase tracking-widest mt-1">
              {f.wall_no ? `Wall ${f.wall_no}` : "New Line"}
            </div>
          </div>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>

        <div className="overflow-y-auto p-6 space-y-5 flex-1">
          <section className="space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Identification</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="S.No / Wall #" required>
                <Input value={f.wall_no} onChange={(e) => set("wall_no", e.target.value)} placeholder="e.g. Wall 07" data-testid="boq-form-wall" />
              </Field>
              <Field label="Title / Item" required>
                <Input value={f.material_name} onChange={(e) => set("material_name", e.target.value)} placeholder="e.g. Frosted Glass Logo" data-testid="boq-form-title" />
              </Field>
              <Field label="Reference / SKU">
                <Input value={f.sku} onChange={(e) => set("sku", e.target.value)} placeholder="optional" />
              </Field>
            </div>
            <Field label="Description">
              <textarea rows="3" value={f.description} onChange={(e) => set("description", e.target.value)}
                        className="w-full p-3 border border-zinc-300 text-sm"
                        placeholder="e.g. 25mm thick acrylic custom cut, duco painted to approved pantone color" />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Dimension (free text)">
                <Input value={f.dimension || ""} onChange={(e) => set("dimension", e.target.value)}
                       placeholder="e.g. 4ft x 2ft" data-testid="boq-form-dimension" />
              </Field>
              <Field label="Location">
                <Input value={f.location || ""} onChange={(e) => set("location", e.target.value)}
                       placeholder="e.g. Reception / 3rd Floor Cabin" data-testid="boq-form-location" />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Production Routing</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                { v: "in_house", title: "In-house Production", desc: "Full pipeline — Design → Production → Dispatch → Installation." },
                { v: "outsourced", title: "Outsourced Production", desc: "Ends at the Pre-Press production file. No internal production, QC, dispatch or install." },
              ].map((opt) => {
                const active = (f.production_type || "in_house") === opt.v;
                return (
                  <button
                    key={opt.v}
                    type="button"
                    onClick={() => set("production_type", opt.v)}
                    data-testid={`boq-form-prodtype-${opt.v}`}
                    className={`text-left p-3 border rounded-md transition-all ${active
                      ? (opt.v === "outsourced"
                          ? "border-violet-500 bg-violet-50 ring-2 ring-violet-100"
                          : "border-emerald-500 bg-emerald-50 ring-2 ring-emerald-100")
                      : "border-zinc-200 hover:border-zinc-400 bg-white"}`}
                  >
                    <div className="flex items-center gap-2">
                      <span className={`w-3.5 h-3.5 rounded-full border-2 flex-shrink-0 ${active
                        ? (opt.v === "outsourced" ? "border-violet-600 bg-violet-600" : "border-emerald-600 bg-emerald-600")
                        : "border-zinc-300"}`} />
                      <span className="font-semibold text-sm">{opt.title}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1 leading-snug pl-5">{opt.desc}</div>
                  </button>
                );
              })}
            </div>
            {(f.production_type || "in_house") === "outsourced" && (
              <div className="flex items-start gap-2 text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-md p-2.5">
                <ExternalLink className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>This line will be handed to an external vendor once the Pre-Press production file is approved. The QC, Production, Dispatch and Installation stages are skipped.</span>
              </div>
            )}
            <button type="button" onClick={() => set("accounts_only", !f.accounts_only)}
              data-testid="boq-form-accounts-only"
              className={`w-full text-left p-3 border rounded-md transition-all ${f.accounts_only
                ? "border-amber-500 bg-amber-50 ring-2 ring-amber-100" : "border-zinc-200 hover:border-zinc-400 bg-white"}`}>
              <div className="flex items-center gap-2">
                <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${f.accounts_only ? "border-amber-600 bg-amber-600" : "border-zinc-300"}`}>
                  {f.accounts_only && <Check className="w-3 h-3 text-white" />}
                </span>
                <span className="font-semibold text-sm">For accounts purpose only</span>
              </div>
              <div className="text-[11px] text-zinc-500 mt-1 leading-snug pl-6">
                A purely financial line (e.g. design fees, transport, consultancy). It skips Design, Pre-Press &amp; Production entirely and can't be sub-divided — it only needs a PO and flows to Accounts.
              </div>
            </button>
          </section>

          <section className="space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Artwork & Wall Measurement</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-mono uppercase tracking-wider text-zinc-600 mb-1">Artwork / Mockup Image</label>
                {f.artwork_image_url && (
                  <img src={f.artwork_image_url} alt="artwork" className="w-full max-h-48 object-contain border border-zinc-200 mb-2" />
                )}
                <FileUpload value={f.artwork_image_url} onChange={(url) => set("artwork_image_url", url)}
                            label="Upload Artwork Image" accept=".png,.jpg,.jpeg,.webp,.heic,.heif"
                            testid="boq-form-image" />
              </div>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Height (inches)">
                    <Input type="number" min="0" step="0.01" value={f.height} onChange={(e) => set("height", e.target.value)} data-testid="boq-form-height" />
                  </Field>
                  <Field label="Width (inches)">
                    <Input type="number" min="0" step="0.01" value={f.width} onChange={(e) => set("width", e.target.value)} data-testid="boq-form-width" />
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Quantity">
                    <Input type="number" min="0" step="0.01" value={f.quantity_required} onChange={(e) => set("quantity_required", e.target.value)} data-testid="boq-form-qty" />
                  </Field>
                  <Field label="Unit">
                    <Select value={f.unit} onChange={(e) => set("unit", e.target.value)} data-testid="boq-form-unit">
                      <optgroup label="Length">
                        <option value="Inches">Inches</option>
                        <option value="Centimeters">Centimeters</option>
                        <option value="Millimeters">Millimeters</option>
                        <option value="Meters">Meters</option>
                        <option value="Feet">Feet</option>
                      </optgroup>
                      <optgroup label="Area">
                        <option value="Square Feet">Square Feet</option>
                        <option value="Square Meters">Square Meters</option>
                      </optgroup>
                      <optgroup label="Count">
                        <option value="Nos">Nos (Numbers)</option>
                        <option value="Pcs">Pieces</option>
                        <option value="Set">Set</option>
                      </optgroup>
                      <optgroup label="Object">
                        <option value="Door">Door</option>
                        <option value="Wall">Wall</option>
                        <option value="Panel">Panel</option>
                        <option value="Sheet">Sheet</option>
                      </optgroup>
                      <option value="Other">Other / Custom</option>
                    </Select>
                  </Field>
                </div>
              </div>
            </div>
          </section>

          {/* Msg 894 — Total Quantity + Commercials (Scope-team Excel layout) */}
          <section className="space-y-3" data-testid="boq-form-commercials">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Total Quantity &amp; Commercials</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Field label="Total Quantity">
                <Input type="number" min="0" step="0.01"
                       value={f.total_quantity ?? ""}
                       onChange={(e) => set("total_quantity", e.target.value)}
                       placeholder={`defaults to ${f.quantity_required || 0}`}
                       data-testid="boq-form-total-qty" />
              </Field>
              {canSeeMoney && (
                <Field label="Rate (₹)">
                  <Input type="number" min="0" step="0.01" value={f.rate ?? 0}
                         onChange={(e) => set("rate", e.target.value)} data-testid="boq-form-rate" />
                </Field>
              )}
              {canSeeMoney && (
                <Field label="Amount (₹) — auto">
                  <div className="w-full h-10 px-3 flex items-center border border-zinc-200 bg-zinc-50 text-sm font-mono font-semibold text-emerald-700"
                       data-testid="boq-form-amount">
                    ₹{computedAmount.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
                  </div>
                </Field>
              )}
            </div>
            <label className="flex items-center gap-2 text-xs text-zinc-600">
              <input type="checkbox" checked={!!f.total_quantity_override}
                     onChange={(e) => set("total_quantity_override", e.target.checked)}
                     data-testid="boq-form-tq-override" />
              Pin this Total Quantity (don't auto-update from PSM site measurements)
            </label>
            {!canSeeMoney && (
              <div className="text-[11px] text-zinc-400 italic">Commercial values are managed by the Scope &amp; Accounts teams.</div>
            )}
          </section>

          <section className="space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Tax · HSN</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="HSN / SAC Code">
                <Input value={f.hsn_code} onChange={(e) => set("hsn_code", e.target.value)}
                       placeholder="e.g. 49111010 (Printed matter)"
                       data-testid="boq-form-hsn" />
              </Field>
              <Field label="HSN Description (optional)">
                <Input value={f.hsn_description} onChange={(e) => set("hsn_description", e.target.value)}
                       placeholder="e.g. Other printed matter, incl. pictures / posters" />
              </Field>
            </div>
          </section>

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Stage Pipeline</div>
              <div className="text-[10px] text-zinc-500">Mark a stage <b>N/A</b> to skip it for this line</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {STAGE_COLS.map((c) => (
                <Field key={c.k} label={c.label}>
                  <Select value={f[c.k] || "pending"} onChange={(e) => set(c.k, e.target.value)} data-testid={`boq-form-${c.k}`}>
                    {STAGE_VALUES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                  </Select>
                </Field>
              ))}
            </div>
          </section>

          <section className="space-y-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Schedule & Notes</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Installation Timeline">
                <Input value={f.installation_timeline} onChange={(e) => set("installation_timeline", e.target.value)} placeholder="e.g. 1-2 Weeks" />
              </Field>
              <Field label="Category (optional)">
                <Input value={f.category} onChange={(e) => set("category", e.target.value)} placeholder="e.g. Vinyl, Acrylic" />
              </Field>
            </div>
            <Field label="Remarks">
              <textarea rows="2" value={f.remarks} onChange={(e) => set("remarks", e.target.value)} className="w-full p-3 border border-zinc-300 text-sm" />
            </Field>
          </section>
        </div>

        <div className="flex gap-2 p-6 border-t border-zinc-200 bg-zinc-50 flex-shrink-0">
          <Btn onClick={() => onSave(f)} data-testid="boq-form-save">{isNew ? "Add Line" : "Update Line"}</Btn>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          {locked && <span className="ml-auto self-center text-xs font-mono uppercase tracking-widest text-rose-600">BOQ is locked — changes will fail until unlocked</span>}
        </div>
      </div>
    </div>
  );
}

// ───────── Site Measurements ─────────
function Measurements({ code }) {
  const [items, setItems] = useState([]);
  const [boq, setBoq] = useState([]);
  const [m, setM] = useState({ boq_item_id: "", location_name: "", length: 0, width: 0, height: 0, area: 0, notes: "" });
  function load() { api.get(`/projects/${code}/measurements`).then((r) => setItems(r.data)); }
  useEffect(load, [code]);
  useEffect(() => { api.get(`/projects/${code}`).then((r) => setBoq(r.data.boq || [])); }, [code]);

  async function add(e) {
    e.preventDefault();
    try {
      await api.post(`/projects/${code}/measurements`, { ...m, length: Number(m.length), width: Number(m.width), height: Number(m.height), area: Number(m.area) });
      toast.success("Saved"); load();
      setM({ boq_item_id: "", location_name: "", length: 0, width: 0, height: 0, area: 0, notes: "" });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function del(id) {
    if (!confirm("Delete?")) return;
    await api.delete(`/projects/${code}/measurements/${id}`); load();
  }
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="card-flat p-4 grid grid-cols-1 sm:grid-cols-7 gap-2 items-end">
        <Field label="BOQ Line"><Select value={m.boq_item_id} onChange={(e) => setM({ ...m, boq_item_id: e.target.value })}>
          <option value="">— optional —</option>
          {boq.map((b) => <option key={b.id} value={b.id}>{b.material_name} ({b.sku || b.spec || ""})</option>)}
        </Select></Field>
        <Field label="Location"><Input value={m.location_name} onChange={(e) => setM({ ...m, location_name: e.target.value })} placeholder="e.g. Main Lobby" /></Field>
        <Field label="Length"><Input type="number" min="0" step="0.01" value={m.length} onChange={(e) => setM({ ...m, length: e.target.value })} /></Field>
        <Field label="Width"><Input type="number" min="0" step="0.01" value={m.width} onChange={(e) => setM({ ...m, width: e.target.value })} /></Field>
        <Field label="Height"><Input type="number" min="0" step="0.01" value={m.height} onChange={(e) => setM({ ...m, height: e.target.value })} /></Field>
        <Field label="Area (auto)"><Input type="number" min="0" step="0.01" value={m.area} onChange={(e) => setM({ ...m, area: e.target.value })} /></Field>
        <Btn type="submit" data-testid="add-measurement"><Plus className="w-4 h-4" /> Add</Btn>
      </form>
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Location</th><th className="text-left p-3">BOQ Line</th><th className="text-right p-3">L × W × H</th><th className="text-right p-3">Area</th><th className="text-left p-3">Notes</th><th className="text-left p-3">By</th><th></th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="7" className="p-6 text-center text-zinc-400">No measurements</td></tr>}
            {items.map((it) => {
              const b = boq.find((x) => x.id === it.boq_item_id);
              return (
                <tr key={it.id} className="border-t border-zinc-100">
                  <td className="p-3">{it.location_name || "—"}</td>
                  <td className="p-3 text-xs">{b ? b.material_name : "—"}</td>
                  <td className="p-3 text-right font-mono">{it.length} × {it.width} × {it.height}</td>
                  <td className="p-3 text-right font-mono">{it.area}</td>
                  <td className="p-3 text-xs">{it.notes}</td>
                  <td className="p-3 text-xs">{it.created_by}</td>
                  <td className="p-3 text-right"><button onClick={() => del(it.id)} className="text-rose-600 text-xs">Delete</button></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────── Material Summary (Procurement) ─────────
function MaterialSummary({ code }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get(`/projects/${code}/material-summary`).then((r) => setData(r.data)); }, [code]);
  if (!data) return <div className="text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-4">
      <div className="card-flat p-4 grid grid-cols-2 sm:grid-cols-3 gap-4">
        <div><div className="text-xs font-mono uppercase tracking-widest text-zinc-500">BOQ Lines</div><div className="font-display text-2xl font-bold mt-1">{data.totals.items_count}</div></div>
        <div><div className="text-xs font-mono uppercase tracking-widest text-zinc-500">Required Material Cost</div><div className="font-display text-2xl font-bold mt-1">₹{data.totals.required_cost.toLocaleString()}</div></div>
      </div>
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Status</th><th className="text-left p-3">Material</th><th className="text-right p-3">Required</th><th className="text-right p-3">Available</th><th className="text-right p-3">Procured</th><th className="text-right p-3">Consumed</th><th className="text-right p-3">Shortage</th><th className="text-right p-3">Total Cost</th></tr>
          </thead>
          <tbody>
            {data.items.length === 0 && <tr><td colSpan="8" className="p-6 text-center text-zinc-400">Add BOQ lines first</td></tr>}
            {data.items.map((s) => (
              <tr key={s.boq_id} className="border-t border-zinc-100">
                <td className="p-3"><StatusLight status={s.status || "not_started"} /></td>
                <td className="p-3"><div>{s.material_name}</div><div className="text-xs text-zinc-500 font-mono">{s.category} · {s.sku || s.spec || ""}</div></td>
                <td className="p-3 text-right font-mono">{s.required} {s.unit}</td>
                <td className="p-3 text-right font-mono text-emerald-700">{s.available}</td>
                <td className="p-3 text-right font-mono">{s.procured}</td>
                <td className="p-3 text-right font-mono">{s.consumed}</td>
                <td className={`p-3 text-right font-mono font-semibold ${s.shortage > 0 ? "text-rose-600" : "text-zinc-400"}`}>{s.shortage}</td>
                <td className="p-3 text-right font-mono">₹{(s.total_cost || 0).toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ───────── Delivery Challans ─────────
function Challans({ code, boq, me }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState([]);
  const [type, setType] = useState("partial");
  const [notes, setNotes] = useState("");
  const [addr, setAddr] = useState("");
  const canCreate = ["admin", "management"].includes(me?.role);
  function load() { api.get(`/projects/${code}/challans`).then((r) => setItems(r.data)); }
  useEffect(load, [code]);

  function toggle(id) { setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]); }
  function toggleAll() {
    if (sel.length === boq.length) setSel([]);
    else setSel(boq.map((b) => b.id));
  }

  async function create() {
    try {
      await api.post(`/projects/${code}/challans`, { challan_type: type, boq_item_ids: type === "full" ? [] : sel, notes, delivery_address: addr });
      toast.success("Challan created"); setOpen(false); setSel([]); setNotes(""); setAddr(""); load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  return (
    <div className="space-y-6">
      {/* Header with single CTA */}
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-orange-600">Project Delivery Folder</div>
          <h2 className="font-display text-xl font-bold tracking-tight mt-1">Delivery Challans &amp; Installation</h2>
          <p className="text-xs text-zinc-500 mt-1">
            DCs are auto-generated line-by-line when production completes — ISM downloads + uploads the signed copy.
          </p>
        </div>
        {/* Phase 34 r12 (Msg 806) — bundled dispatch PDF: one click, all
            ready-to-ship DCs printed in a single A4 PDF for the ISM. */}
        {["admin", "management", "ism", "csm", "psm"].includes(me?.role) && (
          <a
            href={`${process.env.REACT_APP_BACKEND_URL}/api/projects/${code}/dispatch-bundle`}
            target="_blank" rel="noopener noreferrer"
            data-testid="dispatch-bundle-btn"
            title="Open all open DCs as one printable bundle"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-full bg-orange-600 text-white text-sm font-medium hover:bg-orange-700 shadow-md hover:shadow-lg transition-all"
          >
            <Truck className="w-4 h-4" /> Dispatch Bundle PDF
          </a>
        )}
      </div>

      {/* Project DC Folder — line-grouped DCs, install steps, photos */}
      <div className="card-flat p-5">
        <ProjectDCFolder projectCode={code} onReload={load} />
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] flex flex-col border border-zinc-200">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200 flex-shrink-0">
              <h3 className="font-display font-bold text-xl">New Delivery Challan</h3>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto p-6 space-y-3 flex-1">
              <Field label="Challan Type">
                <Select value={type} onChange={(e) => setType(e.target.value)} data-testid="challan-type-select">
                  <option value="partial">Selected line items (multi-select below)</option>
                  <option value="full">Full Project — all BOQ lines</option>
                </Select>
              </Field>
              <Field label="Delivery Address"><Input value={addr} onChange={(e) => setAddr(e.target.value)} placeholder="leave blank to use project location" /></Field>
              <Field label="Notes (single shared note for all selected lines)"><textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full p-3 border border-zinc-300 text-sm" /></Field>
              {type !== "full" && (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-mono uppercase text-zinc-500">
                      Select line items <span className="text-blue-600">({sel.length} of {boq.length} selected)</span>
                    </div>
                    <button type="button" onClick={toggleAll}
                            className="text-xs font-mono uppercase text-blue-700 hover:underline"
                            data-testid="challan-select-all">
                      {sel.length === boq.length ? "Clear all" : "Select all"}
                    </button>
                  </div>
                  <div className="border border-zinc-200 max-h-60 overflow-y-auto" data-testid="challan-line-list">
                    {boq.length === 0 && <div className="p-3 text-sm text-zinc-400">No BOQ lines</div>}
                    {boq.map((b) => (
                      <label key={b.id} className={`flex items-center gap-2 px-3 h-10 text-sm border-b border-zinc-100 cursor-pointer ${sel.includes(b.id) ? "bg-blue-50/60" : "hover:bg-zinc-50"}`}
                             data-testid={`challan-pick-${b.id}`}>
                        <input type="checkbox" checked={sel.includes(b.id)} onChange={() => toggle(b.id)} />
                        <span className="font-mono text-xs text-zinc-500 w-12">{b.wall_no || "—"}</span>
                        <span className="flex-1 truncate"><span className="font-medium">{b.material_name}</span> <span className="text-zinc-500 text-xs">({b.sku || b.spec || "—"})</span></span>
                        <span className="font-mono text-xs">{b.quantity_required} {b.unit}</span>
                      </label>
                    ))}
                  </div>
                  {sel.length === 0 && (
                    <div className="text-xs text-amber-600 mt-2">Select at least one line, or change type to "Full Project".</div>
                  )}
                </div>
              )}
            </div>
            <div className="flex gap-2 p-6 border-t border-zinc-200 bg-zinc-50 flex-shrink-0">
              <Btn onClick={create} data-testid="save-challan-btn">Generate Challan</Btn>
              <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Production({ code, items, reload }) {
  const [n, setN] = useState({ name: "", target_qty: 1, produced_qty: 0, installed_qty: 0, status: "not_started", remarks: "" });
  async function add(e) {
    e.preventDefault();
    try { await api.post(`/projects/${code}/production-items`, { ...n, target_qty: Number(n.target_qty), produced_qty: Number(n.produced_qty), installed_qty: Number(n.installed_qty) }); reload(); toast.success("Added");
      setN({ name: "", target_qty: 1, produced_qty: 0, installed_qty: 0, status: "not_started", remarks: "" });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  async function update(it, field, value) {
    const body = { ...it, [field]: field === "name" || field === "status" || field === "remarks" ? value : Number(value) };
    delete body.id; delete body.project_code; delete body.created_at;
    await api.patch(`/projects/${code}/production-items/${it.id}`, body); reload();
  }
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="card-flat p-4 grid grid-cols-1 sm:grid-cols-6 gap-2 items-end">
        <Field label="Item"><Input value={n.name} onChange={(e) => setN({ ...n, name: e.target.value })} required /></Field>
        <Field label="Target"><Input type="number" min="0" value={n.target_qty} onChange={(e) => setN({ ...n, target_qty: e.target.value })} required /></Field>
        <Field label="Produced"><Input type="number" min="0" value={n.produced_qty} onChange={(e) => setN({ ...n, produced_qty: e.target.value })} /></Field>
        <Field label="Installed"><Input type="number" min="0" value={n.installed_qty} onChange={(e) => setN({ ...n, installed_qty: e.target.value })} /></Field>
        <Field label="Status">
          <Select value={n.status} onChange={(e) => setN({ ...n, status: e.target.value })}>
            {["not_started", "material_pending", "in_production", "partial", "completed", "ready"].map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Btn type="submit" data-testid="add-prod-item"><Plus className="w-4 h-4" /> Add</Btn>
      </form>
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Item</th><th className="text-right p-3">Target</th><th className="text-right p-3">Produced</th><th className="text-right p-3">Installed</th><th className="text-right p-3">Pending</th><th className="text-left p-3">Status</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="6" className="p-6 text-center text-zinc-400">No production items yet</td></tr>}
            {items.map((it) => (
              <tr key={it.id} className="border-t border-zinc-100">
                <td className="p-3">{it.name}</td>
                <td className="p-3 text-right font-mono">{it.target_qty}</td>
                <td className="p-3 text-right"><input type="number" defaultValue={it.produced_qty} onBlur={(e) => update(it, "produced_qty", e.target.value)} className="w-16 h-8 px-2 border border-zinc-300 font-mono text-right text-sm" /></td>
                <td className="p-3 text-right"><input type="number" defaultValue={it.installed_qty} onBlur={(e) => update(it, "installed_qty", e.target.value)} className="w-16 h-8 px-2 border border-zinc-300 font-mono text-right text-sm" /></td>
                <td className="p-3 text-right font-mono">{Math.max(0, it.target_qty - it.installed_qty)}</td>
                <td className="p-3 font-mono uppercase text-xs">{it.status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Installation({ code, inst, users, reload }) {
  const installers = users.filter((u) => u.role === "installer" || u.role === "admin");
  const [f, setF] = useState({
    site_address: inst?.site_address || "",
    scheduled_date: inst?.scheduled_date || "",
    installer_id: inst?.installer_id || "",
    status: inst?.status || "pending",
    photo_urls: (inst?.photo_urls || []).join("\n"),
    remarks: inst?.remarks || "",
  });
  async function save(e) {
    e.preventDefault();
    try {
      await api.post(`/projects/${code}/installation`, {
        ...f, photo_urls: f.photo_urls.split("\n").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Saved"); reload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  return (
    <form onSubmit={save} className="card-flat p-5 max-w-2xl space-y-4">
      <h3 className="font-display font-bold tracking-tight">Installation</h3>
      <Field label="Site Address"><Input value={f.site_address} onChange={(e) => setF({ ...f, site_address: e.target.value })} /></Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Scheduled Date"><Input type="date" value={f.scheduled_date} onChange={(e) => setF({ ...f, scheduled_date: e.target.value })} /></Field>
        <Field label="Installer">
          <Select value={f.installer_id} onChange={(e) => setF({ ...f, installer_id: e.target.value })}>
            <option value="">Unassigned</option>
            {installers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
        </Field>
      </div>
      <Field label="Status">
        <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
          {["pending", "scheduled", "assigned", "in_progress", "partial", "completed", "signoff_pending", "signoff_done"].map((s) => <option key={s} value={s}>{s}</option>)}
        </Select>
      </Field>
      <Field label="Photo URLs (one per line)">
        <textarea rows="4" value={f.photo_urls} onChange={(e) => setF({ ...f, photo_urls: e.target.value })} className="w-full p-3 border border-zinc-300 text-sm font-mono" />
      </Field>
      <Field label="Remarks"><textarea rows="2" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} className="w-full p-3 border border-zinc-300 text-sm" /></Field>
      <Btn type="submit" data-testid="save-installation">Save Installation</Btn>
    </form>
  );
}

function Artists({ code, tasks, users, reload }) {
  const artists = users.filter((u) => u.role === "artist" || u.role === "admin");
  const [t, setT] = useState({ title: "", artist_id: "", status: "pending", remarks: "" });
  async function add(e) {
    e.preventDefault();
    try { await api.post(`/projects/${code}/artist-tasks`, t); reload(); toast.success("Added");
      setT({ title: "", artist_id: "", status: "pending", remarks: "" });
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }
  return (
    <div className="space-y-4">
      <form onSubmit={add} className="card-flat p-4 grid grid-cols-1 sm:grid-cols-5 gap-2 items-end">
        <Field label="Task"><Input value={t.title} onChange={(e) => setT({ ...t, title: e.target.value })} required /></Field>
        <Field label="Artist">
          <Select value={t.artist_id} onChange={(e) => setT({ ...t, artist_id: e.target.value })}>
            <option value="">Unassigned</option>{artists.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
          </Select>
        </Field>
        <Field label="Status">
          <Select value={t.status} onChange={(e) => setT({ ...t, status: e.target.value })}>
            {["pending", "in_progress", "completed"].map((s) => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field label="Remarks"><Input value={t.remarks} onChange={(e) => setT({ ...t, remarks: e.target.value })} /></Field>
        <Btn type="submit"><Plus className="w-4 h-4" /> Add</Btn>
      </form>
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Task</th><th className="text-left p-3">Artist</th><th className="text-left p-3">Status</th><th className="text-left p-3">Remarks</th></tr>
          </thead>
          <tbody>
            {tasks.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-zinc-400">No tasks</td></tr>}
            {tasks.map((tk) => (
              <tr key={tk.id} className="border-t border-zinc-100">
                <td className="p-3">{tk.title}</td>
                <td className="p-3">{nameOf(users, tk.artist_id)}</td>
                <td className="p-3 font-mono uppercase text-xs">{tk.status}</td>
                <td className="p-3 text-xs">{tk.remarks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Consumption({ items, alloc }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <div className="card-flat">
        <div className="px-5 py-4 border-b border-zinc-200"><h3 className="font-display font-bold tracking-tight">Material Consumption</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Date</th><th className="text-left p-3">Stock</th><th className="text-right p-3">Qty</th><th className="text-left p-3">Type</th></tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-zinc-400">No consumption</td></tr>}
            {items.map((u) => (
              <tr key={u.id} className="border-t border-zinc-100">
                <td className="p-3 text-xs font-mono">{(u.created_at || "").slice(0, 10)}</td>
                <td className="p-3 font-mono text-xs">{u.item_code || u.stock_id}</td>
                <td className="p-3 text-right font-mono">{u.quantity_used}</td>
                <td className="p-3 text-xs uppercase">{u.movement_type}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="card-flat">
        <div className="px-5 py-4 border-b border-zinc-200"><h3 className="font-display font-bold tracking-tight">Allocated / Reserved Inventory</h3></div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-xs font-mono uppercase tracking-wider text-zinc-500">
            <tr><th className="text-left p-3">Stock</th><th className="text-left p-3">Material</th><th className="text-right p-3">Reserved</th><th className="text-right p-3">Available</th></tr>
          </thead>
          <tbody>
            {alloc.length === 0 && <tr><td colSpan="4" className="p-6 text-center text-zinc-400">Nothing allocated</td></tr>}
            {alloc.map((i) => (
              <tr key={i.stock_id} className="border-t border-zinc-100">
                <td className="p-3 font-mono text-xs">{i.stock_id}</td>
                <td className="p-3 text-xs">{i.material_name}</td>
                <td className="p-3 text-right font-mono">{i.reserved}</td>
                <td className="p-3 text-right font-mono">{i.available}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ───────── Installer Line-Item Checklists ─────────
function InstallTasks({ code, boq, users }) {
  const installers = users.filter((u) => u.role === "installer" || u.role === "admin");
  const [tasks, setTasks] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { boq_item_id: "", title: "", checklist: [{ item: "", done: false }],
                  status: "not_started", installer_id: "", remarks: "", photo_urls: [] };
  const [f, setF] = useState(empty);

  function load() { api.get(`/projects/${code}/install-tasks`).then((r) => setTasks(r.data)); }
  useEffect(load, [code]);

  function startNew() { setEdit(null); setF(empty); setOpen(true); }
  function startEdit(t) {
    setEdit(t);
    setF({
      boq_item_id: t.boq_item_id || "", title: t.title || "",
      checklist: (t.checklist && t.checklist.length) ? t.checklist : [{ item: "", done: false }],
      status: t.status || "not_started",
      installer_id: t.installer_id || "",
      remarks: t.remarks || "",
      photo_urls: t.photo_urls || [],
    });
    setOpen(true);
  }

  function setCL(i, k, v) {
    setF((s) => ({ ...s, checklist: s.checklist.map((c, idx) => idx === i ? { ...c, [k]: v } : c) }));
  }
  function addCL() { setF((s) => ({ ...s, checklist: [...s.checklist, { item: "", done: false }] })); }
  function rmCL(i) { setF((s) => ({ ...s, checklist: s.checklist.filter((_, idx) => idx !== i) })); }

  async function save() {
    const body = { ...f, checklist: f.checklist.filter((c) => c.item.trim()) };
    try {
      if (edit) await api.patch(`/projects/${code}/install-tasks/${edit.id}`, body);
      else await api.post(`/projects/${code}/install-tasks`, body);
      toast.success(edit ? "Updated" : "Created");
      setOpen(false); load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  async function quickStatus(t, status) {
    const body = { boq_item_id: t.boq_item_id || "", title: t.title, checklist: t.checklist || [],
                   status, installer_id: t.installer_id || "", remarks: t.remarks || "",
                   photo_urls: t.photo_urls || [] };
    try {
      await api.patch(`/projects/${code}/install-tasks/${t.id}`, body);
      toast.success(`Marked ${status.replace("_", " ")}`); load();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  function progress(t) {
    const cl = t.checklist || [];
    if (!cl.length) return 0;
    return Math.round((cl.filter((c) => c.done).length / cl.length) * 100);
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-display font-bold tracking-tight">Installer Checklists · Line-Item Execution</div>
          <div className="text-xs text-zinc-500 mt-0.5">{tasks.length} task(s) · runs in parallel — not stage-wise</div>
        </div>
        <Btn onClick={startNew} data-testid="new-install-task-btn"><Plus className="w-4 h-4" /> New Task</Btn>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {tasks.length === 0 && <div className="card-flat p-6 text-center text-zinc-400 text-sm col-span-full">No installer tasks yet</div>}
        {tasks.map((t) => {
          const b = boq.find((x) => x.id === t.boq_item_id);
          const pct = progress(t);
          return (
            <div key={t.id} className="card-flat p-4 space-y-2" data-testid={`install-task-${t.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{t.title}</div>
                  <div className="text-xs text-zinc-500 font-mono">{b ? `BOQ: ${b.material_name}` : "No BOQ link"}</div>
                </div>
                <StatusLight status={t.status} />
              </div>
              <div className="text-xs">
                Installer: <span className="font-mono">{nameOf(users, t.installer_id) || "—"}</span>
              </div>
              {(t.checklist || []).length > 0 && (
                <div className="space-y-1 pt-1">
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    <span>Checklist</span><span>{pct}%</span>
                  </div>
                  <div className="h-1 bg-zinc-200">
                    <div className="h-full bg-emerald-500" style={{ width: `${pct}%` }} />
                  </div>
                  <ul className="space-y-1 mt-1">
                    {(t.checklist || []).map((c, i) => (
                      <li key={i} className="flex items-center gap-2 text-xs">
                        <span className={`w-3 h-3 inline-block rounded-sm ${c.done ? "bg-emerald-500" : "bg-zinc-300"}`} />
                        <span className={c.done ? "line-through text-zinc-500" : ""}>{c.item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {t.remarks && <div className="text-xs text-zinc-600 italic">"{t.remarks}"</div>}
              <div className="flex gap-2 pt-2 border-t border-zinc-100">
                <button onClick={() => quickStatus(t, "in_progress")}
                  className="text-xs px-2 h-7 border border-amber-300 hover:bg-amber-50 text-amber-700 font-mono uppercase"
                  data-testid={`mark-progress-${t.id}`}>Start</button>
                <button onClick={() => quickStatus(t, "completed")}
                  className="text-xs px-2 h-7 border border-emerald-300 hover:bg-emerald-50 text-emerald-700 font-mono uppercase"
                  data-testid={`mark-done-${t.id}`}>Done</button>
                <button onClick={() => startEdit(t)}
                  className="text-xs px-2 h-7 border border-zinc-300 hover:bg-zinc-50 ml-auto font-mono uppercase">Edit</button>
              </div>
            </div>
          );
        })}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white max-w-2xl w-full max-h-[90vh] flex flex-col border border-zinc-200">
            <div className="flex items-center justify-between p-6 border-b border-zinc-200 flex-shrink-0">
              <h3 className="font-display font-bold text-xl">{edit ? "Edit" : "New"} Installer Task</h3>
              <button onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <div className="overflow-y-auto p-6 space-y-3 flex-1">
              <Field label="Title"><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} required placeholder="e.g. Install lobby ACP cladding" /></Field>
              <div className="grid grid-cols-2 gap-3">
                <Field label="BOQ Line Item">
                  <Select value={f.boq_item_id} onChange={(e) => setF({ ...f, boq_item_id: e.target.value })}>
                    <option value="">— optional —</option>
                    {boq.map((b) => <option key={b.id} value={b.id}>{b.material_name} ({b.sku || b.spec || ""})</option>)}
                  </Select>
                </Field>
                <Field label="Assigned Installer">
                  <Select value={f.installer_id} onChange={(e) => setF({ ...f, installer_id: e.target.value })}>
                    <option value="">Unassigned</option>
                    {installers.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </Select>
                </Field>
              </div>
              <Field label="Status">
                <Select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
                  <option value="not_started">Not Started</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                </Select>
              </Field>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-mono uppercase tracking-wider text-zinc-600">Checklist</label>
                  <button type="button" onClick={addCL} className="text-xs text-blue-700 font-mono uppercase">+ Add Step</button>
                </div>
                <div className="space-y-2">
                  {f.checklist.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input type="checkbox" checked={!!c.done} onChange={(e) => setCL(i, "done", e.target.checked)} />
                      <Input value={c.item} onChange={(e) => setCL(i, "item", e.target.value)} placeholder={`Step ${i + 1}`} />
                      <button type="button" onClick={() => rmCL(i)} className="text-rose-600 text-sm font-mono">×</button>
                    </div>
                  ))}
                </div>
              </div>
              <Field label="Remarks"><textarea rows="2" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} className="w-full p-3 border border-zinc-300 text-sm" /></Field>
            </div>
            <div className="flex gap-2 p-6 border-t border-zinc-200 bg-zinc-50 flex-shrink-0">
              <Btn onClick={save} data-testid="save-install-task-btn">{edit ? "Update" : "Create"}</Btn>
              <Btn variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ───────── Status Updates feed (read-only audit trail) ─────────
function StatusUpdates({ code }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/projects/${code}/audit`).then((r) => { setRows(r.data); setLoading(false); });
  }, [code]);

  function pretty(r) {
    const a = r.action || "update";
    if (a === "boq_update") {
      const fields = (r.fields || []).filter((f) => !f.endsWith("_by") && !f.endsWith("_at") && !f.endsWith("_role") && !f.endsWith("_value"));
      return `${r.user_name} (${r.user_role || "?"}) updated ${fields.join(", ") || "BOQ"} for "${r.material_name || r.boq_id}" ${r.wall_no ? `· Wall ${r.wall_no}` : ""}`;
    }
    if (a === "challan_created") return `Delivery Challan ${r.challan_number} created (${r.items} items)`;
    if (a === "challan_auto_created") return `Auto-DC ${r.challan_number} opened on dispatch`;
    if (a === "challan_line_update") return `${r.user_name} updated DC line on ${r.challan_number}`;
    if (a === "project_activated") return `Project ACTIVATED (${r.items_count} BOQ lines locked)`;
    if (a === "project_reverted_to_draft") return `Project reverted to DRAFT`;
    if (a === "project_completed") return `Project marked COMPLETED`;
    if (a === "boq_add") return `${r.user_name} added BOQ line "${r.material}"`;
    if (a === "boq_version_created" || a === "boq_version_created_finalized") return `BOQ version snapshot${a.endsWith("finalized") ? " (finalized)" : ""}`;
    return `${r.user_name || "?"} · ${a.replace(/_/g, " ")}`;
  }
  function color(r) {
    if ((r.action || "").includes("activated")) return "bg-emerald-50 border-emerald-300 text-emerald-800";
    if ((r.action || "").includes("auto_created")) return "bg-blue-50 border-blue-300 text-blue-800";
    if ((r.action || "").includes("reverted")) return "bg-amber-50 border-amber-300 text-amber-800";
    if ((r.action || "").includes("completed")) return "bg-emerald-50 border-emerald-300 text-emerald-800";
    return "bg-white border-zinc-200 text-zinc-700";
  }

  return (
    <div className="space-y-3">
      <div className="card-flat p-5 flex items-center justify-between">
        <div>
          <div className="font-display font-bold tracking-tight">Status Updates</div>
          <div className="text-xs text-zinc-500 mt-0.5 font-mono">{rows.length} EVENT(S) · MOST RECENT FIRST</div>
        </div>
      </div>
      {loading && <div className="text-zinc-400 p-6 text-sm">Loading…</div>}
      {!loading && rows.length === 0 && <div className="card-flat p-12 text-center text-zinc-400">No events yet</div>}
      <ol className="space-y-2">
        {rows.map((r) => (
          <li key={r.id} className={`border-l-4 px-4 py-3 ${color(r)}`} data-testid={`audit-${r.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="text-sm">{pretty(r)}</div>
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500 whitespace-nowrap">
                {(r.at || "").replace("T", " ").slice(0, 16)}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}


// ───────── Client Login Section ─────────
// Lets admin/management/csm create a client portal login for the project's
// linked client straight from the project page (no need to hop into Users
// + remember to attach client_id manually).
function ClientLoginSection({ project, me, onReload }) {
  const [loginUser, setLoginUser] = useState(undefined);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "Mipl@123", phone: "" });
  const [busy, setBusy] = useState(false);
  const canManage = me && ["admin", "management", "csm"].includes(me.role);

  async function load() {
    if (!project.client_id) { setLoginUser(null); return; }
    try {
      const { data } = await api.get(`/users?role=client`);
      const found = (data || []).find((u) => u.client_id === project.client_id);
      setLoginUser(found || null);
    } catch { setLoginUser(null); }
  }
  useEffect(() => { load(); }, [project.client_id]);

  function openForm() {
    // Pre-fill with the client's contact email (if available) so accounts only
    // need to type the password.
    setForm((f) => ({
      ...f,
      name: project.client_name || "Client",
      email: "",
    }));
    setOpen(true);
  }

  async function save(e) {
    e.preventDefault();
    if (!form.email || !form.password) {
      toast.error("Email and password are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/users", {
        ...form,
        role: "client",
        client_id: project.client_id,
      });
      toast.success("Client login created — share the email + password with them.");
      setOpen(false);
      setForm({ name: "", email: "", password: "Mipl@123", phone: "" });
      load();
      if (onReload) onReload();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  }

  if (loginUser === undefined) return null; // still loading
  if (!canManage && !loginUser) return null;

  return (
    <div className="card-flat p-5 mb-6 border-l-4 border-l-orange-500" data-testid="project-client-login">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Client Portal Access</div>
          {loginUser ? (
            <>
              <div className="font-display text-base font-bold tracking-tight mt-1">
                {loginUser.name} · <span className="font-mono text-sm font-medium text-orange-700">{loginUser.email}</span>
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Has access to the client portal at <span className="font-mono">/my-projects</span>
              </div>
            </>
          ) : (
            <>
              <div className="font-display text-base font-bold tracking-tight mt-1">
                No client login for {project.client_name || "this client"} yet
              </div>
              <div className="text-xs text-zinc-500 mt-0.5">
                Create a portal login so the client can review and approve designs for this project.
              </div>
            </>
          )}
        </div>
        {canManage && !loginUser && (
          <Btn onClick={openForm} data-testid="create-client-login-btn">
            <Plus className="w-4 h-4" /> Create Client Login
          </Btn>
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white max-w-md w-full p-6 space-y-4 border border-zinc-200 rounded-lg shadow-xl">
            <div className="flex items-center justify-between">
              <h3 className="font-display font-bold text-xl">Create login for {project.client_name}</h3>
              <button type="button" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-zinc-600">
              The client will sign in at <span className="font-mono text-xs">/login</span> using the email and password you set here. Share these credentials over a secure channel.
            </p>
            <Field label="Display name" required>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="client-login-name" />
            </Field>
            <Field label="Email (login ID)" required>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required placeholder="contact@clientcompany.com" data-testid="client-login-email" />
            </Field>
            <Field label="Password" required>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required data-testid="client-login-password" />
            </Field>
            <Field label="Phone (optional)">
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="client-login-phone" />
            </Field>
            <div className="flex gap-2 pt-2">
              <Btn type="submit" disabled={busy} data-testid="client-login-save">{busy ? "Creating…" : "Create Login"}</Btn>
              <Btn type="button" variant="secondary" onClick={() => setOpen(false)}>Cancel</Btn>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
